// components/PriceDialog.js

import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableHead, TableRow, TableCell, TableBody, Typography, Tabs, Tab, Box, IconButton, TextField, Checkbox, FormControlLabel, Collapse, Popover, List, ListItem, ListItemIcon, ListItemText, Chip, Tooltip, Switch } from '@mui/material';
import { Upload as UploadIcon, Download as DownloadIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Tune as TuneIcon, Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx/dist/xlsx.full.min.js';
import fittingsData from './fittingsData';
import DEFAULT_FITTING_PRICES from './fittingsCatalog';
import { formatFittingDisplayName } from '../utils/fittingDisplayName';
import { DEFAULT_PIPE_COMBOS, DEFAULT_SQUARE_PIPE_COMBOS, DEFAULT_SQUARE_SHEET_COMBOS, getDn25CatalogPipeSpec, isDn25PipeDiameter } from './pipeCatalog';
import { getDrawingGabaritMm } from './AutoConstructor/hydraulicSeparatorTotalHeight';
import { facingOutletBomParts, pricedNippelMuftaLength } from './AutoConstructor/collectorUtils';

function buildDefaultPrices() {
  // Берём дефолтные цены из отдельного каталога
  return { ...DEFAULT_FITTING_PRICES };
}

function makePipeKey(diameter, thickness, coating) {
  return `${String(diameter)}|${thickness || '-' }|${coating || '-'}`;
}

function makeSquarePipeKey(profile, thickness, coating, type = 'profile') {
  const prefix = type === 'sheet' ? 'SQUARE_SHEET' : 'SQUARE_PROFILE';
  return `${prefix}|${String(profile)}|${thickness || '-'}|${coating || '-'}`;
}

function parseExcelToPrices(file, onParsed) {
  const reader = new FileReader();
  reader.onload = (e) => {
    const data = new Uint8Array(e.target.result);
    const wb = XLSX.read(data, { type: 'array' });
    const itemPrices = {};
    const pipePrices = {};
    
    // Парсим ВСЕ листы, а не только первый
    wb.SheetNames.forEach((sheetName) => {
      const ws = wb.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      
      json.forEach((row) => {
        const type = String(row.type || row.Type || row.Вид || '').trim().toLowerCase();
        if (type === 'pipe' || type === 'труба') {
          const diameter = String(row.diameter || row.Diameter || row.Диаметр || '').trim();
          const thickness = String(row.thickness || row.Thickness || row.Толщина || '').trim();
          const coating = String(row.coating || row.Coating || row.Покрытие || '').trim();
          const rawPrice = String(row.pricePerMeter || row.PricePerMeter || row['Цена/м'] || row.Цена || '').toString();
          const normalized = rawPrice.replace(/\s+/g, '').replace(',', '.');
          const numericOnly = normalized.replace(/[^0-9.-]/g, '');
          const pricePerMeter = parseFloat(numericOnly || '0');
          if (!diameter || !Number.isFinite(pricePerMeter)) return;
          pipePrices[makePipeKey(diameter, thickness || '-', coating || '-')] = pricePerMeter;
          return;
        }
        // Квадратные профили
        if (type === 'square_profile' || type === 'квадратный_профиль' || type === 'профиль') {
          // Читаем из diameter (для единообразия с круглыми трубами)
          const profile = String(row.diameter || row.Diameter || row.Диаметр || row.profile || row.Profile || row.Профиль || '').trim();
          const thickness = String(row.thickness || row.Thickness || row.Толщина || '').trim();
          const coating = String(row.coating || row.Coating || row.Покрытие || '').trim();
          const rawPrice = String(row.pricePerMeter || row.PricePerMeter || row['Цена/м'] || row.Цена || '').toString();
          const normalized = rawPrice.replace(/\s+/g, '').replace(',', '.');
          const numericOnly = normalized.replace(/[^0-9.-]/g, '');
          const pricePerMeter = parseFloat(numericOnly || '0');
          if (!profile || !Number.isFinite(pricePerMeter)) return;
          pipePrices[makeSquarePipeKey(profile, thickness || '-', coating || '-', 'profile')] = pricePerMeter;
          return;
        }
        // Квадратные листы
        if (type === 'square_sheet' || type === 'квадратный_лист' || type === 'лист') {
          // Читаем из diameter (для единообразия с круглыми трубами)
          const profile = String(row.diameter || row.Diameter || row.Диаметр || row.profile || row.Profile || row.Профиль || '').trim();
          const thickness = String(row.thickness || row.Thickness || row.Толщина || '').trim();
          const coating = String(row.coating || row.Coating || row.Покрытие || '').trim();
          const rawPrice = String(row.pricePerMeter || row.PricePerMeter || row['Цена/м'] || row.Цена || '').toString();
          const normalized = rawPrice.replace(/\s+/g, '').replace(',', '.');
          const numericOnly = normalized.replace(/[^0-9.-]/g, '');
          const pricePerMeter = parseFloat(numericOnly || '0');
          if (!profile || !Number.isFinite(pricePerMeter)) return;
          pipePrices[makeSquarePipeKey(profile, thickness || '-', coating || '-', 'sheet')] = pricePerMeter;
          return;
        }
        // Фитинги
        const key = String(row.key || row.Key || row.Код || '').trim();
        const pricingType = String(row.pricingType || row.unit || row.Тип || '').trim();
        const rawPrice = String(row.price || row.Price || row.Цена || '').toString();
        const normalized = rawPrice.replace(/\s+/g, '').replace(',', '.');
        const numericOnly = normalized.replace(/[^0-9.-]/g, '');
        const price = parseFloat(numericOnly || '0');
        if (!key) return;
        
        // Определяем тип ценообразования
        let normalizedType = 'per_piece';
        if (pricingType === 'per_mm' || pricingType === 'мм') {
          normalizedType = 'per_mm';
        } else if (pricingType === 'nippel_formula' || pricingType === 'формула') {
          normalizedType = 'nippel_formula';
        }
        
        if (!Number.isFinite(price)) return;
        
        // Для nippel_formula сохраняем исходную структуру из каталога, обновляя только minPrice
        if (normalizedType === 'nippel_formula') {
          const existingData = DEFAULT_FITTING_PRICES[key];
          if (existingData && existingData.pricingType === 'nippel_formula') {
            itemPrices[key] = { ...existingData, minPrice: price };
          } else {
            itemPrices[key] = { pricingType: normalizedType, price };
          }
        } else {
          itemPrices[key] = { pricingType: normalizedType, price };
        }
      });
    });
    
    onParsed({ items: itemPrices, pipePrices });
  };
  reader.readAsArrayBuffer(file);
}

function exportPricesToExcel(items, allPipeCombos, getPipePricePerMeter, getSquarePipePricePerMeter) {
  const wb = XLSX.utils.book_new();
  
  // Лист по фитингам - экспортируем ВСЕ фитинги из fittingsData
  const itemRows = Object.keys(fittingsData).map((key) => {
    const fitting = fittingsData[key];
    const priceData = items?.[key];
    
    // Определяем тип ценообразования
    let pricingType = 'per_piece';
    let price = 0;
    
    if (priceData) {
      if (priceData.pricingType === 'nippel_formula') {
        // Для ниппелей указываем минимальную цену
        pricingType = 'nippel_formula';
        price = priceData.minPrice || 0;
      } else {
        pricingType = priceData.pricingType || 'per_piece';
        price = priceData.price ?? 0;
      }
    }
    
    return {
      key,
      displayName: fitting.displayName || '',
      category: fitting.category || '',
      pricingType,
      price,
      weight: fitting.weight || '', // Добавляем вес для справки
    };
  }).sort((a, b) => {
    // Сортируем по категории, потом по имени
    if (a.category !== b.category) return a.category.localeCompare(b.category);
    return a.displayName.localeCompare(b.displayName);
  });
  
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(itemRows), 'Фитинги');
  
  // Лист по трубам (круглые, квадратные профили и листы)
  const pipeRows = Array.from(allPipeCombos || []).map((combo) => {
    // Проверяем, является ли это квадратной трубой (профиль или лист)
    if (combo.startsWith('SQUARE_PROFILE|') || combo.startsWith('SQUARE_SHEET|')) {
      const parts = combo.split('|');
      const type = parts[0]; // SQUARE_PROFILE или SQUARE_SHEET
      const profile = parts[1];
      const thickness = parts[2];
      const coating = parts[3];
      
      const squareType = type === 'SQUARE_SHEET' ? 'sheet' : 'profile';
      const priceInfo = getSquarePipePricePerMeter(
        profile, 
        thickness === '-' ? undefined : thickness, 
        coating === '-' ? undefined : coating, 
        squareType
      );
      
      // Для листов priceInfo - это объект с формулой, для профилей - число
      let pricePerMeter = 0;
      if (squareType === 'sheet' && typeof priceInfo === 'object') {
        pricePerMeter = priceInfo.pricePerMeter || 0;
      } else {
        pricePerMeter = Number(priceInfo) || 0;
      }
      
      return {
        type: squareType === 'sheet' ? 'square_sheet' : 'square_profile',
        diameter: profile, // Используем diameter вместо profile для единообразия
        thickness: thickness === '-' ? '' : thickness,
        coating: coating === '-' ? '' : coating,
        pricePerMeter,
      };
    } else {
      // Круглая труба
      const [diameter, thickness, coating] = combo.split('|');
      return {
        type: 'pipe',
        diameter,
        thickness: thickness === '-' ? '' : thickness,
        coating: coating === '-' ? '' : coating,
        pricePerMeter: getPipePricePerMeter(diameter, thickness === '-' ? undefined : thickness, coating === '-' ? undefined : coating),
      };
    }
  });
  
  if (pipeRows.length > 0) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(pipeRows), 'Трубы');
  }
  
  XLSX.writeFile(wb, 'prices.xlsx');
}

function PriceDialog({ open, onClose, getObjects, prices, onPricesChange, version, resetManualMode, calculationSettings, onCalculationSettingsChange, showWeldMarkers = true, onShowWeldMarkersChange, drawingMetadata, onWeldingTotalChange }) {
  const fileInputRef = useRef(null);
  const syncTimeoutRef = useRef(null);
  const isInitializedRef = useRef(false);
  const lastMetadataRef = useRef(null); // Для отслеживания изменений metadata
  const mirrorItemsLoadedRef = useRef(false); // Для отслеживания загрузки mirrorPolishingItems
  const [objectsSnapshot, setObjectsSnapshot] = useState({ fittings: [], cylinders: [], welds: [] });
  const [activeTab, setActiveTab] = useState(0); // 0: Стоимость, 1: Комплектующие, 2: Сварка, 3: Резка
  const [weldPriceMarkup, setWeldPriceMarkup] = useState(8); // Повышающий коэффициент в процентах
  const [worksTaxPercent, setWorksTaxPercent] = useState(60); // Налог на общие работы в процентах
  const [worksExpanded, setWorksExpanded] = useState(false); // Раскрыты ли общие работы
  const [pipePriceMarkup, setPipePriceMarkup] = useState(10); // Плавающие цены труб в процентах
  const [thermometerCaps, setThermometerCaps] = useState(2); // Заглушки термометр
  const [packaging, setPackaging] = useState(100); // Упаковка (может быть автоматически рассчитана)
  const [mirrorPolishingItems, setMirrorPolishingItems] = useState([]); // Слесарка за зеркало [{diameter, hours}]
  
  // Автоматический расчет упаковки на основе габарита из метаданных (max длина системы, высота ГР)
  // Срабатывает при изменении метаданных (после генерации нового чертежа)
  useEffect(() => {
    const gabaritMm = getDrawingGabaritMm(drawingMetadata);
    const prevGabaritMm = getDrawingGabaritMm(lastMetadataRef.current);
    const metadataChanged = gabaritMm !== prevGabaritMm;
    
    if (gabaritMm > 0 && metadataChanged) {
      // Формула: до 500мм = 50, от 500 до 1000мм = 100, от 1000 до 1500мм = 150, и т.д.
      // Каждые 500мм добавляем 50 рублей
      const calculatedPackaging = Math.ceil(gabaritMm / 500) * 50;
      
      // Обновляем упаковку
      setPackaging(calculatedPackaging);
      
      // Сохраняем текущие метаданные
      lastMetadataRef.current = drawingMetadata;
      
      // Синхронизируем с calculationSettings (с задержкой, чтобы не конфликтовать с инициализацией)
      setTimeout(() => {
        if (onCalculationSettingsChange) {
          onCalculationSettingsChange({
            ...calculationSettings,
            packaging: calculatedPackaging,
          });
        }
      }, 100);
    }
  }, [drawingMetadata, calculationSettings, onCalculationSettingsChange]);
  
  // Настройки теперь приходят из пропсов calculationSettings
  // Если пропсов нет (старая версия или ошибка), используем дефолты локально (но лучше так не делать)
  const noConduit = calculationSettings?.noConduit ?? false;
  const oversizedClassic = calculationSettings?.oversizedClassic ?? false;
  const priceCoefficient = calculationSettings?.priceCoefficient ?? 4;
  
  const [additive, setAdditive] = useState(false); // Присадка (может быть ручной или авто)
  const [calculatedAdditive, setCalculatedAdditive] = useState(false); // Автоматически рассчитанное значение присадки
  const [perWeldAdditiveMode, setPerWeldAdditiveMode] = useState(false); // Режим присадки на отдельные швы
  const [perWeldAdditiveIds, setPerWeldAdditiveIds] = useState([]); // ID швов с присадкой
  const [perWeldAdditiveAnchor, setPerWeldAdditiveAnchor] = useState(null); // Якорь для Popover
  const [manualWeldMode, setManualWeldMode] = useState(false); // Режим ручного ввода суммы сварки
  const [manualWeldTotal, setManualWeldTotal] = useState(0); // Ручная сумма сварки
  const [flangesCount, setFlangesCount] = useState(0); // Количество фланцев (всегда авто)
  const [elbowsCount, setElbowsCount] = useState(0); // Количество отводов (всегда авто)
  
  // Автоматически рассчитанное значение для "Более 25кг/2м"
  const [calculatedHeavyCount, setCalculatedHeavyCount] = useState(0);
  const [calculatedThermometerCaps, setCalculatedThermometerCaps] = useState(0);
  
  // Итоговое значение: ручное или автоматическое
  const heavyOrLongCount = (calculationSettings?.manualHeavyCount !== null && calculationSettings?.manualHeavyCount !== undefined)
    ? calculationSettings.manualHeavyCount
    : calculatedHeavyCount;
  
  // Итоговое значение заглушек термометра: ручное или автоматическое
  const finalThermometerCaps = (calculationSettings?.manualThermometerCaps !== null && calculationSettings?.manualThermometerCaps !== undefined)
    ? calculationSettings.manualThermometerCaps
    : calculatedThermometerCaps;
  
  // Итоговое значение присадки: ручное или автоматическое
  const finalAdditive = (calculationSettings?.manualAdditive !== null && calculationSettings?.manualAdditive !== undefined)
    ? calculationSettings.manualAdditive
    : calculatedAdditive;

  const [weldCoefficient, setWeldCoefficient] = useState(1); // Коэффициент для итоговой суммы

  // Редактирование дополнительной строки: id строки или 'new' для новой
  const [editingExtraId, setEditingExtraId] = useState(null);
  const [editingExtraData, setEditingExtraData] = useState({ name: '', qty: '', length: '', price: '' });

  // Вспомогательные геттеры для overrides и extra из calculationSettings
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const componentRowOverrides = useMemo(() => calculationSettings?.componentRowOverrides || {}, [calculationSettings?.componentRowOverrides]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const extraComponentRows = useMemo(() => calculationSettings?.extraComponentRows || [], [calculationSettings?.extraComponentRows]);

  const saveComponentRowOverrides = useCallback((overrides) => {
    onCalculationSettingsChange?.({ ...calculationSettings, componentRowOverrides: overrides });
  }, [calculationSettings, onCalculationSettingsChange]);

  const saveExtraComponentRows = useCallback((rows) => {
    onCalculationSettingsChange?.({ ...calculationSettings, extraComponentRows: rows });
  }, [calculationSettings, onCalculationSettingsChange]);

  const formatRUB = (val) => new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(Number(val || 0));
  // Источник цен на трубы: Excel (prices.pipePrices) или дефолтный каталог
  const effectivePipeOverrides = useMemo(() => {
    const merged = { ...DEFAULT_PIPE_COMBOS, ...DEFAULT_SQUARE_PIPE_COMBOS, ...DEFAULT_SQUARE_SHEET_COMBOS };
    return (prices && prices.pipePrices) ? prices.pipePrices : merged;
  }, [prices]);

  const getPipePricePerMeter = useCallback((diameter, thickness, coating) => {
    const thick = thickness || '-';
    const coat = coating || '-';
    const keyDirect = makePipeKey(diameter, thick, coat);
    let override = effectivePipeOverrides[keyDirect];
    if (Number.isFinite(Number(override))) return Number(override);
    // ⌀33,7: в каталоге одна строка (x3 «Зеркало») — для прайса всегда она, если нет явного совпадения (например из Excel)
    if (isDn25PipeDiameter(diameter)) {
      const dn = getDn25CatalogPipeSpec();
      const keyDn25 = makePipeKey(dn.diameter, dn.thickness, dn.coating);
      override = effectivePipeOverrides[keyDn25];
      if (Number.isFinite(Number(override))) return Number(override);
    }
    return 0;
  }, [effectivePipeOverrides]);

  const getSquarePipePricePerMeter = useCallback((profile, thickness, coating, type = 'profile') => {
    const key = makeSquarePipeKey(profile, thickness || '-', coating || '-', type);
    const override = effectivePipeOverrides[key];
    
    // Для листов возвращаем объект с ценообразованием, для профилей - число
    if (type === 'sheet' && override && typeof override === 'object') {
      return override; // { basePrice, segmentPrice, segmentLength }
    }
    
    return Number.isFinite(Number(override)) ? Number(override) : 0;
  }, [effectivePipeOverrides]);

  const effectivePrices = useMemo(() => {
    const items = (prices && prices.items) ? prices.items : prices;
    const defaults = buildDefaultPrices();
    // Всегда возвращаем полный перечень фитингов: дефолты + пользовательские цены
    return { ...defaults, ...(items || {}) };
  }, [prices]);

  // Инициализация локальных state из calculationSettings при открытии диалога
  useEffect(() => {
    if (!open) {
      // При закрытии сбрасываем флаг инициализации
      isInitializedRef.current = false;
      return;
    }
    
    // Инициализируем локальные state из calculationSettings ТОЛЬКО при открытии
    if (calculationSettings) {
      // Для присадки: если есть ручное значение, используем его, иначе автоматическое (если есть)
      if (calculationSettings.manualAdditive !== null && calculationSettings.manualAdditive !== undefined) {
        setAdditive(calculationSettings.manualAdditive);
      } else if (calculationSettings.additive !== undefined) {
        setAdditive(calculationSettings.additive);
      }
      if (calculationSettings.perWeldAdditiveMode !== undefined) {
        setPerWeldAdditiveMode(calculationSettings.perWeldAdditiveMode);
      }
      if (calculationSettings.perWeldAdditiveIds !== undefined) {
        setPerWeldAdditiveIds(calculationSettings.perWeldAdditiveIds || []);
      }
      if (calculationSettings.manualWeldMode !== undefined) {
        setManualWeldMode(calculationSettings.manualWeldMode);
      }
      if (calculationSettings.manualWeldTotal !== undefined) {
        setManualWeldTotal(calculationSettings.manualWeldTotal || 0);
      }
      
      if (calculationSettings.weldPriceMarkup !== undefined) setWeldPriceMarkup(calculationSettings.weldPriceMarkup);
      if (calculationSettings.worksTaxPercent !== undefined) setWorksTaxPercent(calculationSettings.worksTaxPercent);
      // НЕ инициализируем weldCoefficient из calculationSettings - он всегда рассчитывается автоматически
      if (calculationSettings.thermometerCaps !== undefined) setThermometerCaps(calculationSettings.thermometerCaps);
      if (calculationSettings.packaging !== undefined) setPackaging(calculationSettings.packaging);
      if (calculationSettings.pipePriceMarkup !== undefined) setPipePriceMarkup(calculationSettings.pipePriceMarkup);
      // mirrorPolishingItems инициализируется в отдельном useEffect для корректной работы с localStorage
    }
    
    // Устанавливаем флаг, что инициализация завершена
    isInitializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Инициализируем ТОЛЬКО при изменении open (открытие/закрытие)

  // Загрузка объектов при открытии диалога
  useEffect(() => {
    if (!open) {
      // Сбрасываем флаг при закрытии диалога
      mirrorItemsLoadedRef.current = false;
      return;
    }
    
    try {
      const snap = typeof getObjects === 'function' ? (getObjects() || { fittings: [], cylinders: [], welds: [] }) : { fittings: [], cylinders: [], welds: [] };
      setObjectsSnapshot(snap);
    } catch (e) {
      setObjectsSnapshot({ fittings: [], cylinders: [], welds: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getObjects, open, version]);

  // Отдельный эффект для mirrorPolishingItems — срабатывает при любом изменении version,
  // независимо от того открыт диалог или нет, чтобы не пропустить запись в localStorage
  useEffect(() => {
    const storedMirrorItems = localStorage.getItem('mirrorPolishingItems');

    if (storedMirrorItems) {
      try {
        const newItems = JSON.parse(storedMirrorItems);

        // Если пустой массив - очищаем mirrorPolishingItems (переключение на матовый)
        if (newItems.length === 0) {
          setMirrorPolishingItems([]);
          localStorage.removeItem('mirrorPolishingItems');
        } else {
          // Объединяем с существующими данными из calculationSettings
          const existingItems = calculationSettings?.mirrorPolishingItems || [];

          const itemsMap = new Map();
          existingItems.forEach(item => {
            itemsMap.set(item.diameter, item);
          });
          newItems.forEach(item => {
            if (!itemsMap.has(item.diameter)) {
              itemsMap.set(item.diameter, item);
            }
            // Если диаметр уже есть — оставляем существующий (с сохранёнными часами)
          });

          const mergedItems = Array.from(itemsMap.values());
          setMirrorPolishingItems(mergedItems);
          localStorage.removeItem('mirrorPolishingItems');
        }
      } catch (e) {
        console.error('Ошибка при загрузке данных о зеркальном отзеркаливании:', e);
      }
    } else if (!mirrorItemsLoadedRef.current && calculationSettings?.mirrorPolishingItems?.length > 0) {
      // Нет данных в localStorage, но есть в calculationSettings — загружаем один раз
      setMirrorPolishingItems(calculationSettings.mirrorPolishingItems);
      mirrorItemsLoadedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, open]);

  // Сброс ручного режима при загрузке нового чертежа
  useEffect(() => {
    if (resetManualMode && typeof onCalculationSettingsChange === 'function') {
      onCalculationSettingsChange({
        noConduit: true, // По умолчанию включено
        oversizedClassic: true, // По умолчанию включено
        manualHeavyCount: null,
        manualThermometerCaps: null,
        manualAdditive: null,
        perWeldAdditiveMode: false,
        perWeldAdditiveIds: [],
        manualWeldMode: false,
        manualWeldTotal: 0,
        priceCoefficient: 4
      });
    }
  }, [resetManualMode, onCalculationSettingsChange]);

  // Автоматическое заполнение полей сварки на основе объектов
  useEffect(() => {
    if (!open) return;
    
    const fittings = objectsSnapshot.fittings || [];
    const cylinders = objectsSnapshot.cylinders || [];
    
    // Категории для автоматической галочки "Присадка"
    // НЕ включаем "Заглушки" - они обрабатываются отдельно (круглые vs квадратные)
    const additiveCategories = ['Фланцы', 'Бортшайбы', 'Переходы концентрические', 'Отводы'];
    
    // Подсчёт фитингов по категориям
    let flangesTotal = 0;
    let elbowsTotal = 0;
    let hasAdditiveItems = false;
    
    fittings.forEach(f => {
      // Исключаем крепежи из всех расчетов
      if (f.type === 'fastener') {
        return;
      }
      
      // Исключаем скрытые фитинги (фиктивные для IGES отверстий)
      if (f.isHidden) {
        return;
      }
      
      const itemKey = f.itemKey;
      const fittingInfo = fittingsData[itemKey];
      const category = fittingInfo?.category || '';
      
      // Проверка для присадки - основные категории
      if (additiveCategories.includes(category)) {
        hasAdditiveItems = true;
      }
      
      // Специальная проверка для заглушек прямых:
      // - Круглые (Zaglushka_PR_42.4 ... Zaglushka_PR_101.6) - НЕ включают присадку
      // - Квадратные (Zaglushka_PR_20x20 ... Zaglushka_PR_150x150) - ВКЛЮЧАЮТ присадку
      if (itemKey && itemKey.startsWith('Zaglushka_PR_')) {
        // Проверяем, квадратная ли это заглушка (содержит "x" или "х" в размере)
        const sizepart = itemKey.replace('Zaglushka_PR_', '');
        if (sizepart.includes('x') || sizepart.includes('х')) {
          // Квадратная заглушка - включаем присадку
          hasAdditiveItems = true;
        }
        // Круглые заглушки (42.4, 48.3, 60.3 и т.д.) - НЕ включаем присадку
      }
      
      // Эллиптические заглушки включают присадку
      if (itemKey && itemKey.startsWith('Zaglushka_EL_')) {
        hasAdditiveItems = true;
      }
      
      // Подсчёт фланцев
      if (category === 'Фланцы') {
        flangesTotal += 1;
      }
      
      // Подсчёт отводов
      if (category === 'Отводы') {
        elbowsTotal += 1;
      }
    });
    
    // Подсчёт центральных фитингов "с 2-х сторон" (для заглушек термометра)
    // Фитинги "с 2-х сторон" создаются парами с одинаковыми координатами
    // Группируем фитинги по координатам и считаем только пары
    let thermometerCapsCount = 0;
    const centerFittings = fittings.filter(f => f.isSelfFacing && f.isCenterFitting && !f.isHidden);
    
    // Группируем по координатам (используем groupX, groupY если есть, иначе x, y)
    const positionMap = new Map();
    centerFittings.forEach(f => {
      const posX = f.groupX !== undefined ? f.groupX : f.x;
      const posY = f.groupY !== undefined ? f.groupY : f.y;
      const key = `${posX}-${posY}`;
      if (!positionMap.has(key)) {
        positionMap.set(key, []);
      }
      positionMap.get(key).push(f);
    });
    
    // Считаем только позиции, где есть 2 фитинга (пара)
    positionMap.forEach(group => {
      if (group.length === 2) {
        thermometerCapsCount += 1;
      }
    });
    
    // Проверка квадратных труб (профиль или лист) - включают присадку
    cylinders.forEach(c => {
      const ps = c.pipeSpec;
      if (ps?.shape === 'square') {
        hasAdditiveItems = true;
      }
    });
    
    // Подсчёт общего веса (примерный расчёт)
    let totalWeight = 0;
    
    // Вес фитингов (исключаем крепежи)
    const density = 0.00000793; // кг/мм³ для AISI 304
    
    fittings.forEach(f => {
      // Исключаем крепежи из расчета веса
      if (f.type === 'fastener') {
        return;
      }
      
      // Исключаем скрытые фитинги (фиктивные для IGES отверстий)
      if (f.isHidden) {
        return;
      }
      
      const itemKey = f.itemKey;
      const fittingInfo = fittingsData[itemKey];
      if (!fittingInfo) return;
      
      // Для ниппелей и муфт рассчитываем вес по формуле
      if (itemKey.startsWith('Nippel_') || itemKey.startsWith('Mufta_')) {
        const D = Number(fittingInfo.diameter); // наружный диаметр (мм)
        const t = 3; // толщина стенки 3 мм для всех фитингов
        const priced = pricedNippelMuftaLength(f, cylinders);
        const L = priced != null ? priced : Number(f.height || fittingInfo.length || 0);
        
        if (Number.isFinite(D) && Number.isFinite(L)) {
          // Формула для круглой трубы: Вес (кг) = π/4 × (D² - d²) × L × density
          const d = D - 2 * t; // внутренний диаметр
          const crossSectionArea = (Math.PI / 4) * (D * D - d * d); // площадь сечения (мм²)
          const weight = crossSectionArea * L * density;
          totalWeight += weight;
        }
      } else {
        // Для остальных фитингов берем фиксированный вес из fittingsData
        totalWeight += Number(fittingInfo.weight) || 0;
      }

      facingOutletBomParts(f, cylinders).forEach((part) => {
        if (part.isPipe) {
          const D = Number(part.diameter);
          const t = parseFloat(String(part.thickness).replace('x', ''));
          const d = D - 2 * t;
          if (Number.isFinite(D) && Number.isFinite(t) && d > 0) {
            totalWeight += (Math.PI / 4) * (D * D - d * d) * part.length * density;
          }
          return;
        }
        totalWeight += Number(fittingsData[part.itemKey]?.weight) || 0;
      });
    });
    
    // Вес крепежей KRGB (AISI 304, плотность 7.93 г/см³)
    const krgbFasteners = fittings.filter(f => f.type === 'fastener' && f.itemKey === 'KRGB');
    krgbFasteners.forEach(f => {
      const fastenerHeight = f.fastenerHeight || f.height || 400; // высота крепежа (мм)
      const pipeDiameter = f.pipeDiameter || f.width || 406.4; // диаметр трубы (мм)
      
      // 1. Вес 4 профилей 80×40 толщиной 2 мм
      const profileA = 80; // мм
      const profileB = 40; // мм
      const profileT = 2; // мм (толщина стенки)
      const profileOuterArea = profileA * profileB; // внешняя площадь
      const profileInnerArea = (profileA - 2 * profileT) * (profileB - 2 * profileT); // внутренняя площадь
      const profileCrossSectionArea = profileOuterArea - profileInnerArea; // площадь сечения (мм²)
      const profileWeight = profileCrossSectionArea * fastenerHeight * density; // вес одного профиля (кг)
      const totalProfileWeight = 4 * profileWeight; // вес 4 профилей (кг)
      
      // 2. Вес 1 трубы круглой толщиной x3 (3 мм), длиной 120 мм
      const pipeD = pipeDiameter; // наружный диаметр (мм)
      const pipeT = 3; // толщина стенки (мм)
      const pipeL = 120; // длина трубы (мм)
      const piped = pipeD - 2 * pipeT; // внутренний диаметр
      const pipeCrossSectionArea = (Math.PI / 4) * (pipeD * pipeD - piped * piped); // площадь сечения (мм²)
      const pipeWeight = pipeCrossSectionArea * pipeL * density; // вес трубы (кг)
      
      // 3. Общий вес крепежа KRGB
      const krgbWeight = totalProfileWeight + pipeWeight;
      totalWeight += krgbWeight;
    });
    
    // Вес труб (AISI 304, плотность 7.93 г/см³)
    cylinders.forEach(c => {
      const ps = c.pipeSpec;
      
      // Круглые трубы
      if (ps?.shape === 'round' && ps.diameter && ps.thickness) {
        const D = Number(ps.diameter);
        const thicknessStr = String(ps.thickness).replace('x', '');
        const t = Number(thicknessStr);
        
        const dw = Math.abs((c.width || 0) - D);
        const dh = Math.abs((c.height || 0) - D);
        const L = dw <= dh ? (c.height || 0) : (c.width || 0);
        
        const d = D - 2 * t;
        const crossSectionArea = (Math.PI / 4) * (D * D - d * d);
        const weight = crossSectionArea * L * density;
        totalWeight += weight;
      }
      
      // Квадратные трубы (профиль или лист)
      if (ps?.shape === 'square' && ps.profile && ps.thickness) {
        const profileParts = String(ps.profile).split('x');
        if (profileParts.length === 2) {
          const a = Number(profileParts[0]);
          const b = Number(profileParts[1]);
          const t = Number(String(ps.thickness));
          const L = Math.max(c.width || 0, c.height || 0);
          
          if (ps.squareType === 'sheet') {
            // ЛИСТ
            let sheetWidth;
            if (a === 60 && b === 30) {
              sheetWidth = 112.3;
            } else if (a === 80 && b === 40) {
              sheetWidth = 152.3;
            } else {
              sheetWidth = 2 * (a + b) - 4 * t;
            }
            const weight = sheetWidth * L * t * density;
            totalWeight += weight;
          } else {
            // ПРОФИЛЬ
            const outerArea = a * b;
            const innerArea = (a - 2*t) * (b - 2*t);
            const crossSectionArea = outerArea - innerArea;
            const weight = crossSectionArea * L * density;
            totalWeight += weight;
          }
        }
      }
    });
    
    // Расчёт для "Более 25кг/2м"
    // 1. За вес: за каждые 25кг добавляем +1
    const countByWeight = Math.floor(totalWeight / 25);
    
    // 2. За габарит: за каждые 2000мм (2м) добавляем +1
    let countByLength = 0;
    const gabaritMm = getDrawingGabaritMm(drawingMetadata);
    if (gabaritMm > 0) {
      countByLength = Math.floor(gabaritMm / 2000);
    }
    
    // 3. Итоговое значение = сумма по весу и по длине
    const heavyCount = countByWeight + countByLength;
    
    // Обновляем состояния
    setCalculatedAdditive(hasAdditiveItems); // Сохраняем автоматически рассчитанное значение
    
    // Если нет ручного значения, обновляем additive
    if (calculationSettings?.manualAdditive === null || calculationSettings?.manualAdditive === undefined) {
      setAdditive(hasAdditiveItems);
    }
    
    setFlangesCount(flangesTotal);
    setElbowsCount(elbowsTotal);
    setCalculatedThermometerCaps(thermometerCapsCount); // Автоматический расчет заглушек термометра
    
    // Если нет ручного значения, обновляем thermometerCaps
    if (calculationSettings?.manualThermometerCaps === null || calculationSettings?.manualThermometerCaps === undefined) {
      setThermometerCaps(thermometerCapsCount);
    }
    
    // Обновляем автоматическое значение
    setCalculatedHeavyCount(heavyCount);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectsSnapshot, open, drawingMetadata]);

  // Синхронизация локальных настроек с calculationSettings (с debounce)
  useEffect(() => {
    // НЕ синхронизируем, пока не завершена инициализация
    if (!isInitializedRef.current) {
      return;
    }
    
    // Очищаем предыдущий таймер
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    
    // Устанавливаем новый таймер для синхронизации через 500мс
    syncTimeoutRef.current = setTimeout(() => {
      if (onCalculationSettingsChange) {
        onCalculationSettingsChange({
          ...calculationSettings,
          additive,
          weldPriceMarkup,
          worksTaxPercent,
          weldCoefficient,
          thermometerCaps,
          packaging,
          pipePriceMarkup,
          mirrorPolishingItems,
          perWeldAdditiveMode,
          perWeldAdditiveIds,
          manualWeldMode,
          manualWeldTotal
        });
      }
    }, 500);
    
    // Cleanup при размонтировании
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additive, weldPriceMarkup, worksTaxPercent, weldCoefficient, thermometerCaps, packaging, pipePriceMarkup, JSON.stringify(mirrorPolishingItems), perWeldAdditiveMode, JSON.stringify(perWeldAdditiveIds), manualWeldMode, manualWeldTotal]); // Синхронизируем при изменении любого локального значения

  // Автоматический расчёт коэффициента на основе чекбоксов
  useEffect(() => {
    let coef = 1; // Базовый коэффициент
    
    if (noConduit) coef += 0.25;        // Нет кондуктора: +0.25
    if (oversizedClassic) coef += 0.25; // Габариты больше CLASSIC: +0.25
    // Присадка: +1 только в глобальном режиме; в per-weld режиме применяется к отдельным швам
    if (finalAdditive && !perWeldAdditiveMode) coef += 1;
    
    setWeldCoefficient(coef);
  }, [noConduit, oversizedClassic, finalAdditive, calculatedAdditive, calculationSettings, perWeldAdditiveMode]);

  const rows = useMemo(() => {
    const groups = new Map();
    (objectsSnapshot.fittings || []).forEach((f) => {
      // Для крепежей KRGB добавляем комплектующие
      if (f.type === 'fastener' && f.itemKey === 'KRGB') {
        
        // Добавляем 4 профиля 80х40 длиной = высота крепежа
        // Высота может быть в fastenerHeight (для новых) или в height (для старых)
        const profileLength = f.fastenerHeight || f.height || 400; // высота крепежа
        const profileKey = 'PROFILE_80x40_KRGB';
        if (!groups.has(profileKey)) {
          groups.set(profileKey, { 
            key: profileKey, 
            qty: 0, 
            sumLength: 0, 
            isKRGBProfile: true,
            displayName: 'Профиль 80×40 (для KRGB)'
          });
        }
        const profileGroup = groups.get(profileKey);
        profileGroup.qty += 4; // 4 профиля на один крепеж
        profileGroup.sumLength += profileLength * 4;
        
        // Добавляем 120 мм трубы выбранного диаметра (+ 200 на резку)
        const pipeDiameter = f.pipeDiameter || f.width || 406.4;
        const pipeLength = 120; // мм базовая длина (+ 200 на резку при расчете)
        const pipeKey = `PIPE_KRGB_${pipeDiameter}`;
        if (!groups.has(pipeKey)) {
          groups.set(pipeKey, {
            key: pipeKey,
            qty: 0,
            sumLength: 0,
            isKRGBPipe: true,
            diameter: pipeDiameter,
            displayName: `Труба ⌀${pipeDiameter} (для KRGB)`
          });
        }
        const pipeGroup = groups.get(pipeKey);
        pipeGroup.qty += 1;
        pipeGroup.sumLength += pipeLength;
        
        // Остальные крепежи исключаем из комплектующих
        return;
      }
      
      // Исключаем остальные крепежи из комплектующих
      if (f.type === 'fastener') {
        return;
      }
      
      // Исключаем скрытые фитинги (фиктивные для IGES отверстий)
      if (f.isHidden) {
        return;
      }
      
      const itemKey = f.itemKey || 'UNKNOWN';
      const fittingInfo = fittingsData[itemKey];
      const category = fittingInfo?.category || '';
      const isNippelOrMufta = category === 'Ниппели' || category === 'Муфты';

      // Лицевые и термометры: вылет с заходом в трубу, как у контура.
      // Высота объекта у них — размер картинки, не длина для резки.
      let length;
      if (itemKey.startsWith('Nippel_') || itemKey.startsWith('Mufta_')) {
        length = pricedNippelMuftaLength(f, objectsSnapshot.cylinders)
          ?? Number(fittingInfo?.length || f.height || 0);
      } else if (f.isSelfFacing) {
        length = Number(fittingInfo?.length || f.height || 0);
      } else {
        length = Number(f.height || 0);
      }

      // Для ниппелей и муфт группируем отдельно по длине — чтобы разные длины шли разными строками
      // Для ключа группировки округляем до 2 знаков чтобы избежать float-погрешностей
      const roundedLength = Math.round(length * 100) / 100;
      const key = isNippelOrMufta ? `${itemKey}__len_${roundedLength}` : itemKey;

      if (!groups.has(key)) groups.set(key, { key, itemKey, qty: 0, sumLength: 0, lengths: [], isNippelOrMufta, unitLength: isNippelOrMufta ? roundedLength : null });
      const g = groups.get(key);
      g.qty += 1;
      
      g.sumLength += length;
      g.lengths.push(length); // Сохраняем отдельные длины для формульного расчета

      facingOutletBomParts(f, objectsSnapshot.cylinders).forEach((part) => {
        if (part.isPipe) {
          const runLength = part.length;
          const baseKey = `PIPE_ROUND_${part.diameter}_${part.thickness}_${part.coating}`;
          const pipeKey = `${baseKey}__len_${runLength}`;
          if (!groups.has(pipeKey)) {
            groups.set(pipeKey, {
              key: pipeKey,
              itemKey: baseKey,
              qty: 0,
              sumLength: 0,
              isPipe: true,
              pipeType: 'round',
              diameter: String(part.diameter),
              thickness: part.thickness,
              coating: part.coating,
              unitLength: runLength,
            });
          }
          const pipeGroup = groups.get(pipeKey);
          pipeGroup.qty += 1;
          pipeGroup.sumLength += runLength;
          return;
        }
        const extraInfo = fittingsData[part.itemKey];
        if (!groups.has(part.itemKey)) {
          groups.set(part.itemKey, {
            key: part.itemKey,
            itemKey: part.itemKey,
            qty: 0,
            sumLength: 0,
            lengths: [],
            isNippelOrMufta: false,
            unitLength: null,
          });
        }
        const extra = groups.get(part.itemKey);
        extra.qty += 1;
        extra.sumLength += Number(extraInfo?.length) || 0;
      });
    });

    // Трубы из цилиндров: группируем по типу (круглые/квадратные)
    (objectsSnapshot.cylinders || []).forEach((c) => {
      if (c.isHidden) { return; } // Исключаем скрытые цилиндры (соединительные трубы)
      const ps = c.pipeSpec;
      
      // Круглые трубы
      if (ps && ps.shape === 'round' && typeof ps.diameter === 'number') {
        const diameterKey = String(ps.diameter);
        const thickness = ps.thickness || '-';
        const coating = ps.coating || '-';
        // Длина — размер перпендикулярный диаметру
        const dw = Math.abs((c.width || 0) - Number(ps.diameter));
        const dh = Math.abs((c.height || 0) - Number(ps.diameter));
        const geomLength = dw <= dh ? (c.height || 0) : (c.width || 0);
        // Производственная длина = геометрия + insertionDepth (глубина вставки в гидроразделитель)
        const runLength = Math.round((geomLength + (c.insertionDepth || 0)) * 100) / 100;
        // Группируем с учётом длины — одинаковые трубы одной длины в одну строку
        const baseKey = `PIPE_ROUND_${diameterKey}_${thickness}_${coating}`;
        const key = `${baseKey}__len_${runLength}`;
        if (!groups.has(key)) groups.set(key, { key, itemKey: baseKey, qty: 0, sumLength: 0, isPipe: true, pipeType: 'round', diameter: diameterKey, thickness, coating, unitLength: runLength });
        const g = groups.get(key);
        g.qty += 1;
        g.sumLength += runLength;
      }
      
      // Квадратные трубы (профиль и лист)
      if (ps && ps.shape === 'square' && ps.profile) {
        const profile = String(ps.profile);
        const thickness = ps.thickness || '-';
        const coating = ps.coating || '-';
        const squareType = ps.squareType || 'profile'; // profile|sheet
        // Для квадратных труб длина - это больший размер
        const runLength = Math.round(Math.max(c.width || 0, c.height || 0) * 100) / 100;
        // Группируем с учётом длины
        const baseKey = `PIPE_SQUARE_${squareType.toUpperCase()}_${profile}_${thickness}_${coating}`;
        const key = `${baseKey}__len_${runLength}`;
        if (!groups.has(key)) groups.set(key, { key, itemKey: baseKey, qty: 0, sumLength: 0, isPipe: true, pipeType: 'square', profile, thickness, coating, squareType, unitLength: runLength });
        const g = groups.get(key);
        g.qty += 1;
        g.sumLength += runLength;
      }
    });
    const result = Array.from(groups.values());
    // Сортируем: одинаковые itemKey (ниппели/муфты разной длины) идут рядом
    result.sort((a, b) => {
      const aKey = a.itemKey || a.key;
      const bKey = b.itemKey || b.key;
      if (aKey < bKey) return -1;
      if (aKey > bKey) return 1;
      // Одинаковый itemKey — сортируем по длине
      return (a.unitLength || 0) - (b.unitLength || 0);
    });
    return result;
  }, [objectsSnapshot]);

  const total = useMemo(() => {
    const CUTTING_ALLOWANCE = 100;
    
    return rows.reduce((acc, r) => {
      const ov = componentRowOverrides[r.key] || {};
      // Скрытые строки не включаем в итог
      if (ov.hidden) return acc;
      // Если есть ручной итог — используем его напрямую
      if (ov.total != null) return acc + Number(ov.total);

      const effQty = ov.qty != null ? Number(ov.qty) : r.qty;

      // Комплектующие KRGB (профили) — 4 профиля на крепёж; масштабируем по кол-ву
      if (r.isKRGBProfile) {
        const totalLengthWithCutting = r.sumLength + (r.qty * CUTTING_ALLOWANCE);
        const pricePerMeter = getSquarePipePricePerMeter('80x40', '2', 'Матовый', 'profile');
        const perMeter = typeof pricePerMeter === 'number' ? pricePerMeter : 0;
        const autoAmt = totalLengthWithCutting * (perMeter / 1000);
        // При изменении кол-ва — пересчитываем пропорционально
        return acc + (ov.qty != null && r.qty > 0 ? autoAmt / r.qty * effQty : autoAmt);
      }
      
      // Комплектующие KRGB (трубы)
      if (r.isKRGBPipe) {
        const KRGB_PIPE_CUTTING = 200;
        const totalLengthWithCutting = r.sumLength + (r.qty * KRGB_PIPE_CUTTING);
        const perMeter = getPipePricePerMeter(r.diameter, 'x3', 'Матовый');
        const autoAmt = totalLengthWithCutting * (perMeter / 1000);
        return acc + (ov.qty != null && r.qty > 0 ? autoAmt / r.qty * effQty : autoAmt);
      }
      
      if (r.isPipe) {
        if (r.pipeType === 'round') {
          const perMeter = getPipePricePerMeter(r.diameter, r.thickness === '-' ? undefined : r.thickness, r.coating === '-' ? undefined : r.coating);
          // Пересчитываем длину с учётом нового кол-ва: sumLength/qty * effQty + effQty*CUTTING
          const unitLen = r.qty > 0 ? r.sumLength / r.qty : 0;
          const adjLength = unitLen * effQty + effQty * CUTTING_ALLOWANCE;
          return acc + adjLength * (perMeter / 1000);
        } else if (r.pipeType === 'square') {
          const priceInfo = getSquarePipePricePerMeter(r.profile, r.thickness === '-' ? undefined : r.thickness, r.coating === '-' ? undefined : r.coating, r.squareType);
          if (r.squareType === 'sheet' && typeof priceInfo === 'object' && priceInfo.pricePerMeter) {
            const lengthInMm = r.sumLength;
            const lengthInMeters = lengthInMm / 1000;
            const autoAmt = priceInfo.pricePerMeter * lengthInMeters * (priceInfo.coefficient1 + priceInfo.coefficient2 / lengthInMm);
            return acc + (ov.qty != null && r.qty > 0 ? autoAmt / r.qty * effQty : autoAmt);
          }
          const perMeter = typeof priceInfo === 'number' ? priceInfo : 0;
          const unitLen = r.qty > 0 ? r.sumLength / r.qty : 0;
          const adjLength = unitLen * effQty + effQty * CUTTING_ALLOWANCE;
          return acc + adjLength * (perMeter / 1000);
        }
      }
      const priceCfg = effectivePrices[r.itemKey || r.key] || { pricingType: 'per_piece', price: 0 };

      if (priceCfg.pricingType === 'nippel_formula') {
        let totalAmount = 0;
        (r.lengths || []).forEach(length => {
          if (length <= priceCfg.baseLength) {
            totalAmount += priceCfg.minPrice;
          } else {
            const pipePricePerMeter = getPipePricePerMeter(
              priceCfg.pipeDiameter, priceCfg.pipeThickness, priceCfg.pipeCoating || 'Матовый'
            );
            const extraLength = length - priceCfg.baseLength;
            const pipeCost = (pipePricePerMeter * extraLength) / 1000;
            const weldCost = priceCfg.weldCost * priceCfg.weldCoefficient;
            totalAmount += priceCfg.minPrice + pipeCost + weldCost;
          }
        });
        // Если кол-во изменено вручную — пересчитываем пропорционально
        if (ov.qty != null && r.qty > 0) totalAmount = totalAmount / r.qty * effQty;
        return acc + totalAmount;
      }
      
      const isPerMm = priceCfg.pricingType === 'per_mm';
      return acc + (isPerMm ? r.sumLength * (priceCfg.price || 0) : effQty * (priceCfg.price || 0));
    }, 0) + extraComponentRows.reduce((acc, er) => {
      const qty = Number(er.qty) || 0;
      const len = Number(er.length) || 0;
      const price = Number(er.price) || 0;
      return acc + (len > 0 ? (len / 1000) * price * qty : qty * price);
    }, 0);
  }, [rows, effectivePrices, getPipePricePerMeter, getSquarePipePricePerMeter, componentRowOverrides, extraComponentRows]);

  // Расчёт суммы только труб (для плавающих цен)
  const pipesTotal = useMemo(() => {
    const CUTTING_ALLOWANCE = 100;
    
    return rows.reduce((acc, r) => {
      if (!r.isPipe) return acc;
      
      if (r.pipeType === 'round') {
        const perMeter = getPipePricePerMeter(r.diameter, r.thickness === '-' ? undefined : r.thickness, r.coating === '-' ? undefined : r.coating);
        const perMm = perMeter / 1000;
        const totalLengthWithCutting = r.sumLength + (r.qty * CUTTING_ALLOWANCE);
        return acc + (totalLengthWithCutting * perMm);
      } else if (r.pipeType === 'square') {
        const priceInfo = getSquarePipePricePerMeter(r.profile, r.thickness === '-' ? undefined : r.thickness, r.coating === '-' ? undefined : r.coating, r.squareType);
        
        if (r.squareType === 'sheet' && typeof priceInfo === 'object' && priceInfo.pricePerMeter) {
          const lengthInMm = r.sumLength;
          const lengthInMeters = lengthInMm / 1000;
          const totalPrice = priceInfo.pricePerMeter * lengthInMeters * (priceInfo.coefficient1 + priceInfo.coefficient2 / lengthInMm);
          return acc + totalPrice;
        }
        
        const perMeter = typeof priceInfo === 'number' ? priceInfo : 0;
        const perMm = perMeter / 1000;
        const totalLengthWithCutting = r.sumLength + (r.qty * CUTTING_ALLOWANCE);
        return acc + (totalLengthWithCutting * perMm);
      }
      return acc;
    }, 0);
  }, [rows, getPipePricePerMeter, getSquarePipePricePerMeter]);

  // Расчёт итоговой суммы сварки
  const weldTotal = useMemo(() => {
    // Фиксированные цены для конкретных диаметров
    const fixedWeldPrices = {
      'Прямая': {
        21.3: 13,
        24.5: 13,
        26.9: 14,
        29.9: 15,
        33.7: 15,
        38: 19.4,
        42.4: 19.4,
        48: 25,
        48.3: 25,
        53.9: 27.6,
        60.3: 27.6,
        66: 34.2,
        76.1: 34.2,
        88.9: 42.3,
        114.3: 43.8,
        129: 61,
        159: 72.5,
        273: 129,
        default: 0.5  // Для всех остальных диаметров - по формуле (₽/мм)
      },
      'Сферическая': {
        21.3: 29,
        24.5: 29,
        26.9: 29,
        29.9: 32,
        33.7: 32,
        38: 34.2,
        42.4: 34.2,
        48: 38,
        48.3: 38,
        53.9: 44.6,
        60.3: 44.6,
        66: 55,
        76.1: 55,
        88.9: 64.8,
        114.3: 80,
        129: 99,
        159: 111,
        273: 198,
        default: 0.85  // Для всех остальных диаметров - по формуле (₽/мм)
      }
    };
    
    const welds = objectsSnapshot.welds || [];
    const cylinders = objectsSnapshot.cylinders || [];
    const markupMultiplier = 1 + (weldPriceMarkup / 100);
    
    // Функция для проверки, относится ли сварка к скрытому цилиндру
    const isWeldHidden = (weld) => {
      if (weld.obj1?.objectType === 'cylinder') {
        const cyl = cylinders.find(c => c.id === weld.obj1.objectId);
        if (cyl?.isHidden) return true;
      }
      if (weld.obj2?.objectType === 'cylinder') {
        const cyl = cylinders.find(c => c.id === weld.obj2.objectId);
        if (cyl?.isHidden) return true;
      }
      return false;
    };
    
    const profileSheetWelds = welds.filter(w => w.type === 'ПрофильЛист' && !isWeldHidden(w));
    let regularWelds = welds.filter(w => w.type !== 'ПрофильЛист' && !isWeldHidden(w));
    
    // Если есть сварки "ПрофильЛист", объединяем пары сварок профиль+лист
    if (profileSheetWelds.length > 0) {
      const weldsBySize = new Map(); // displayDiameter -> [welds]
      const otherWelds = [];
      
      // Группируем сварки по размеру
      regularWelds.forEach(w => {
        if (w.displayDiameter === '80х40' || w.displayDiameter === '60х30') {
          const key = w.displayDiameter;
          if (!weldsBySize.has(key)) {
            weldsBySize.set(key, []);
          }
          weldsBySize.get(key).push(w);
        } else {
          otherWelds.push(w);
        }
      });
      
      const newWelds = [...otherWelds];
      
      // Объединяем пары сварок 80х40 -> 80х80
      const welds80x40 = weldsBySize.get('80х40') || [];
      for (let i = 0; i < welds80x40.length; i += 2) {
        if (i + 1 < welds80x40.length) {
          // Есть пара - объединяем в 80х80
          // Периметр квадрата 80х80 = 4*80 = 320
          // Эквивалентный диаметр = периметр / π = 320 / 3.14159 ≈ 101.86
          const perimeter80x80 = 4 * 80;
          const equivalentDiameter80x80 = perimeter80x80 / Math.PI;
          // length для сварок - это эквивалентный диаметр, поэтому используем equivalentDiameter80x80
          newWelds.push({
            ...welds80x40[i],
            displayDiameter: '80х80',
            diameter: equivalentDiameter80x80,
            length: equivalentDiameter80x80
          });
        } else {
          // Нечетная сварка - оставляем как есть
          newWelds.push(welds80x40[i]);
        }
      }
      
      // Объединяем пары сварок 60х30 -> 60х60
      const welds60x30 = weldsBySize.get('60х30') || [];
      for (let i = 0; i < welds60x30.length; i += 2) {
        if (i + 1 < welds60x30.length) {
          // Есть пара - объединяем в 60х60
          // Периметр квадрата 60х60 = 4*60 = 240
          // Эквивалентный диаметр = периметр / π = 240 / 3.14159 ≈ 76.39
          const perimeter60x60 = 4 * 60;
          const equivalentDiameter60x60 = perimeter60x60 / Math.PI;
          // length для сварок - это эквивалентный диаметр, поэтому используем equivalentDiameter60x60
          newWelds.push({
            ...welds60x30[i],
            displayDiameter: '60х60',
            diameter: equivalentDiameter60x60,
            length: equivalentDiameter60x60
          });
        } else {
          // Нечетная сварка - оставляем как есть
          newWelds.push(welds60x30[i]);
        }
      }
      
      regularWelds = newWelds;
    }
    
    // Базовая стоимость шва (без коэффициента сварки)
    const getWeldBasePrice = (w) => {
      const type = w.type || 'Прямая';
      const diameter = w.diameter || 0;
      const fixedPrice = fixedWeldPrices[type]?.[diameter];
      if (fixedPrice !== undefined) {
        return fixedPrice * markupMultiplier;
      }
      const pricePerMm = fixedWeldPrices[type]?.default || 0.5;
      return (w.length || 0) * pricePerMm * markupMultiplier;
    };

    let totalWeldCost = 0;
    
    // Обычные сварки
    regularWelds.forEach(w => {
      const basePrice = getWeldBasePrice(w);
      // В режиме присадки по швам: для выбранных швов коэффициент = weldCoefficient + 1, иначе weldCoefficient
      const effectiveCoef = (perWeldAdditiveMode && perWeldAdditiveIds.includes(w.id))
        ? weldCoefficient + 1
        : weldCoefficient;
      totalWeldCost += basePrice * effectiveCoef;
    });
    
    // ПрофильЛист - каждая сварка считается как 2 (лист приваривается с двух сторон)
    profileSheetWelds.forEach(w => {
      const basePrice = 2 * 100;
      const effectiveCoef = (perWeldAdditiveMode && perWeldAdditiveIds.includes(w.id))
        ? weldCoefficient + 1
        : weldCoefficient;
      totalWeldCost += basePrice * effectiveCoef;
    });
    
    const flangesExtra = flangesCount * 100;
    const elbowsExtra = elbowsCount * 250;
    const heavyExtra = heavyOrLongCount * 1000;
    
    return totalWeldCost + flangesExtra + elbowsExtra + heavyExtra;
  }, [objectsSnapshot.welds, objectsSnapshot.cylinders, weldPriceMarkup, weldCoefficient, flangesCount, elbowsCount, heavyOrLongCount, perWeldAdditiveMode, perWeldAdditiveIds]);

  // Передаем итоговую сумму сварки наверх для таблицы цеха (в ручном режиме — введённую сумму)
  useEffect(() => {
    if (onWeldingTotalChange) {
      onWeldingTotalChange(manualWeldMode ? manualWeldTotal : weldTotal);
    }
  }, [weldTotal, onWeldingTotalChange, manualWeldMode, manualWeldTotal]);

  // Расчёт итоговой суммы резки
  const cuttingTotal = useMemo(() => {
    const getCuttingPrice = (diameter) => {
      const d = Number(diameter) || 0;
      if (d <= 48.3) return 30;
      if (d <= 60.3) return 40;
      if (d <= 168.3) return 70;
      if (d >= 200 && d <= 300) return 1500;
      if (d > 300 && d <= 400) return 2000;
      if (d > 400 && d <= 550) return 3500;
      if (d > 550 && d <= 650) return 5000;
      return 0;
    };
    
    const isPricePerPipe = (diameter) => {
      const d = Number(diameter) || 0;
      return d >= 200;
    };
    
    const welds = objectsSnapshot.welds || [];
    const cylinders = objectsSnapshot.cylinders || [];
    
    // Группируем трубы по диаметру (круглые и квадратные)
    const pipeGroups = new Map();
    cylinders.forEach(c => {
      if (c.isHidden) { return; } // Исключаем скрытые цилиндры (соединительные трубы)
      const ps = c.pipeSpec;
      if (ps?.shape === 'round' && ps.diameter) {
        const diameter = Number(ps.diameter);
        if (!pipeGroups.has(diameter)) {
          pipeGroups.set(diameter, { pipes: [], baseCuts: 0 });
        }
        const group = pipeGroups.get(diameter);
        group.pipes.push(c);
        group.baseCuts += 2;
      }
      // Для квадратных профилей вычисляем эквивалентный диаметр через периметр
      // Формула: периметр = 2*(a+b), эквивалентный диаметр = периметр / π
      if (ps?.shape === 'square' && ps.profile) {
        const parts = String(ps.profile).split('x');
        if (parts.length === 2) {
          const a = Number(parts[0]) || 0;
          const b = Number(parts[1]) || 0;
          const perimeter = 2 * (a + b); // Периметр профиля
          const diameter = perimeter / Math.PI; // Эквивалентный диаметр
          if (!pipeGroups.has(diameter)) {
            pipeGroups.set(diameter, { pipes: [], baseCuts: 0 });
          }
          const group = pipeGroups.get(diameter);
          group.pipes.push(c);
          // Для листов (squareType: 'sheet') не добавляем базовые резки
          if (ps.squareType !== 'sheet') {
            group.baseCuts += 2;
          }
        }
      }
    });
    
    // Функция проверки бокового соединения
    const isSideConnection = (weld, cylinder) => {
      if (!cylinder || !weld) return false;
      const ps = cylinder.pipeSpec;
      if (!ps) return false;
      
      const pipeWidth = cylinder.width || 0;
      const pipeHeight = cylinder.height || 0;
      const pipeX = cylinder.x || 0;
      const pipeY = cylinder.y || 0;
      
      // Определяем размер трубы/профиля для проверки ориентации
      // Для круглых труб - диаметр, для квадратных - меньшая сторона (реальный размер в Canvas)
      let pipeSize = 0;
      if (ps.shape === 'round') {
        pipeSize = Number(ps.diameter) || 0;
      } else if (ps.shape === 'square' && ps.profile) {
        const parts = String(ps.profile).split('x');
        if (parts.length === 2) {
          const a = Number(parts[0]) || 0;
          const b = Number(parts[1]) || 0;
          // Используем меньшую сторону для проверки ориентации (реальный размер в Canvas)
          pipeSize = Math.min(a, b);
        }
      }
      
      if (pipeSize === 0) return false;
      
      const isVertical = Math.abs(pipeWidth - pipeSize) < 5;
      const isHorizontal = Math.abs(pipeHeight - pipeSize) < 5;
      
      const weldX = weld.x || 0;
      const weldY = weld.y || 0;
      const EDGE_THRESHOLD = pipeSize * 0.6;
      
      if (isHorizontal) {
        const distToLeft = Math.abs(weldX - pipeX);
        const distToRight = Math.abs(weldX - (pipeX + pipeWidth));
        return !(distToLeft < EDGE_THRESHOLD || distToRight < EDGE_THRESHOLD);
      }
      
      if (isVertical) {
        const distToTop = Math.abs(weldY - pipeY);
        const distToBottom = Math.abs(weldY - (pipeY + pipeHeight));
        return !(distToTop < EDGE_THRESHOLD || distToBottom < EDGE_THRESHOLD);
      }
      
      return true;
    };
    
    // Резы от сварок
    const weldCutsByDiameter = new Map();
    
    welds.forEach(w => {
      if (w.type === 'ПрофильЛист') return;
      
      // Проверяем, что оба объекта сварки не скрытые
      const cyl1 = w.obj1?.objectType === 'cylinder' ? cylinders.find(c => c.id === w.obj1.objectId) : null;
      const cyl2 = w.obj2?.objectType === 'cylinder' ? cylinders.find(c => c.id === w.obj2.objectId) : null;
      
      // Если хотя бы один из объектов скрытый - пропускаем
      if ((cyl1 && cyl1.isHidden) || (cyl2 && cyl2.isHidden)) {
        return;
      }
      
      let countsCut = false;
      
      // Проверяем obj1 (если это цилиндр)
      if (w.obj1?.objectType === 'cylinder') {
        const cyl = cylinders.find(c => c.id === w.obj1.objectId);
        if (cyl && !cyl.isHidden && cyl.pipeSpec?.shape === 'round' && isSideConnection(w, cyl)) {
          countsCut = true;
        }
        // Для квадратных профилей также считаем боковые резы
        if (cyl && !cyl.isHidden && cyl.pipeSpec?.shape === 'square' && isSideConnection(w, cyl)) {
          countsCut = true;
        }
      }
      
      // Проверяем obj2 (если это цилиндр)
      if (w.obj2?.objectType === 'cylinder') {
        const cyl = cylinders.find(c => c.id === w.obj2.objectId);
        if (cyl && !cyl.isHidden && cyl.pipeSpec?.shape === 'round' && isSideConnection(w, cyl)) {
          countsCut = true;
        }
        // Для квадратных профилей также считаем боковые резы
        if (cyl && !cyl.isHidden && cyl.pipeSpec?.shape === 'square' && isSideConnection(w, cyl)) {
          countsCut = true;
        }
      }
      
      // Специальная обработка для дополнительных выходов гидроразделителя
      // Они всегда создают боковое соединение (отверстие в стенке трубы)
      // Горизонтальные выходы: source === 'hydraulicSeparatorOutlet'
      // Вертикальные выходы: source === 'selfFacing' + один из объектов - fitting с isCenterFitting
      if (w.source === 'hydraulicSeparatorOutlet') {
        countsCut = true;
      } else if (w.source === 'selfFacing') {
        // Проверяем, что это выход гидроразделителя (центральный фитинг)
        const allFittings = objectsSnapshot.fittings || [];
        const fitting1 = w.obj1?.objectType === 'fitting' ? allFittings.find(f => f.id === w.obj1.objectId) : null;
        const fitting2 = w.obj2?.objectType === 'fitting' ? allFittings.find(f => f.id === w.obj2.objectId) : null;
        
        if ((fitting1 && fitting1.isCenterFitting) || (fitting2 && fitting2.isCenterFitting)) {
          countsCut = true;
        }
      }
      
      if (countsCut) {
        const weldDiameter = Number(w.diameter) || 0;
        if (weldDiameter > 0) {
          weldCutsByDiameter.set(weldDiameter, (weldCutsByDiameter.get(weldDiameter) || 0) + 1);
        }
      }
    });
    
    // Группируем по категориям
    const cutsByCategory = new Map();
    const largePipesByCategory = new Map();
    
    // Базовые резы
    Array.from(pipeGroups.entries()).forEach(([diameter, data]) => {
      const price = getCuttingPrice(diameter);
      const perPipe = isPricePerPipe(diameter);
      const category = perPipe ? 
        (diameter <= 300 ? '200-300' : diameter <= 400 ? '300-400' : diameter <= 550 ? '400-550' : '550-650') :
        (diameter <= 48.3 ? 'до 48.3' : diameter <= 60.3 ? 'до 60.3' : 'до 168.3');
      
      if (perPipe) {
        if (!largePipesByCategory.has(category)) {
          largePipesByCategory.set(category, new Set());
        }
        data.pipes.forEach(pipe => largePipesByCategory.get(category).add(pipe.id));
      } else {
        if (!cutsByCategory.has(category)) {
          cutsByCategory.set(category, { count: 0, price });
        }
        cutsByCategory.get(category).count += data.baseCuts;
      }
    });
    
    // Резы от сварок
    Array.from(weldCutsByDiameter.entries()).forEach(([diameter, cuts]) => {
      const price = getCuttingPrice(diameter);
      const perPipe = isPricePerPipe(diameter);
      
      if (!perPipe) {
        const category = diameter <= 48.3 ? 'до 48.3' : diameter <= 60.3 ? 'до 60.3' : 'до 168.3';
        if (!cutsByCategory.has(category)) {
          cutsByCategory.set(category, { count: 0, price });
        }
        cutsByCategory.get(category).count += cuts;
      }
    });
    
    // Большие трубы
    Array.from(largePipesByCategory.entries()).forEach(([category, pipeIds]) => {
      const sampleDiameter = category === '200-300' ? 200 : category === '300-400' ? 301 : category === '400-550' ? 401 : 551;
      const price = getCuttingPrice(sampleDiameter);
      cutsByCategory.set(category, { count: pipeIds.size, price });
    });
    
    // Итого
    let totalCost = 0;
    Array.from(cutsByCategory.values()).forEach(data => {
      totalCost += data.count * data.price;
    });
    
    return totalCost;
  }, [objectsSnapshot.welds, objectsSnapshot.cylinders, objectsSnapshot.fittings]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Стоимость</Typography>
          <Box>
            <IconButton 
              onClick={() => fileInputRef.current?.click()} 
              title="Загрузить Excel цен"
              size="small"
            >
              <UploadIcon />
            </IconButton>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              parseExcelToPrices(file, (mapping) => {
                if (typeof onPricesChange === 'function') onPricesChange({ ...(prices || {}), ...mapping });
              });
              e.target.value = '';
            }} />
            <IconButton 
              onClick={() => {
                const allCombos = new Set(Object.keys(effectivePipeOverrides || {}));
                exportPricesToExcel(effectivePrices, allCombos, getPipePricePerMeter, getSquarePipePricePerMeter);
              }}
              title="Выгрузить текущие цены"
              size="small"
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tab label="Стоимость" />
          <Tab label="Комплектующие" />
          <Tab label="Сварка" />
          <Tab label="Резка" />
        </Tabs>

        {/* Вкладка "Стоимость" */}
        {activeTab === 0 && (() => {
          const fontSize = '0.875rem';
          const effectiveWeldTotal = manualWeldMode ? manualWeldTotal : weldTotal;
          const otherWorks = effectiveWeldTotal; // Прочие работы = Сварка
          const baseWorks = effectiveWeldTotal + otherWorks; // База общих работ = Сварка + Прочие работы
          const taxMultiplier = 1 + (worksTaxPercent / 100);
          const totalWorks = baseWorks * taxMultiplier; // Общие работы с налогом
          const thermometerCapsCost = finalThermometerCaps * 20; // Заглушки термометр: количество × 20₽
          
          const extraCosts = thermometerCapsCost + packaging; // Дополнительные расходы (заглушки + упаковка)
          const grandTotal = total + totalWorks + cuttingTotal + extraCosts; // Себестоимость с учетом доп. расходов
          
          const pipePriceExtra = pipesTotal * (pipePriceMarkup / 100); // Плавающие цены труб
          
          // Слесарка за зеркало: часы × 3000₽/час - идет напрямую в цену клиента
          // Используем часы из первого элемента, так как все диаметры имеют одинаковое количество часов
          const mirrorPolishingCost = mirrorPolishingItems.length > 0 ? (mirrorPolishingItems[0].hours * 3000) : 0;
          
          const clientPrice = grandTotal * priceCoefficient + pipePriceExtra + mirrorPolishingCost; // Цена клиента
          
          return (
            <Box sx={{ fontSize }}>
              {/* Коэффициент и плавающие цены - отдельный блок */}
              <Box sx={{ 
                mb: 2, 
                p: 1.5, 
                backgroundColor: 'rgba(0, 0, 0, 0.03)', 
                borderRadius: 1, 
                border: '0.0625rem solid rgba(0, 0, 0, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize, fontWeight: 500 }}>Коэффициент:</Typography>
                  <TextField
                    type="number"
                    value={priceCoefficient}
                    onChange={(e) => onCalculationSettingsChange?.({ 
                      ...calculationSettings, 
                      priceCoefficient: Number(e.target.value) || 0,
                      // Сохраняем текущие локальные значения
                      additive,
                      weldPriceMarkup,
                      worksTaxPercent,
                      weldCoefficient,
                      thermometerCaps,
                      packaging,
                      pipePriceMarkup,
                      mirrorPolishingItems
                    })}
                    size="small"
                    inputProps={{ step: 0.1, min: 0 }}
                    sx={{ 
                      width: 60, 
                      '& .MuiInputBase-input': { fontSize, padding: '0.25rem 0.5rem', textAlign: 'center' } 
                    }}
                  />
          </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize, fontWeight: 500 }}>Плавающие цены труб:</Typography>
                  <TextField
                    type="number"
                    value={pipePriceMarkup}
                    onChange={(e) => setPipePriceMarkup(Number(e.target.value) || 0)}
                    size="small"
                    inputProps={{ step: 1, min: 0 }}
                    sx={{ 
                      width: 60, 
                      '& .MuiInputBase-input': { fontSize, padding: '0.25rem 0.5rem', textAlign: 'center' } 
                    }}
                  />
                  <Typography sx={{ fontSize, fontWeight: 500 }}>%</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize, fontWeight: 500 }}>Заглушки термометр:</Typography>
                  <TextField
                    type="number"
                    value={finalThermometerCaps}
                    onChange={(e) => {
                      const val = Number(e.target.value) || 0;
                      onCalculationSettingsChange?.({ 
                        ...calculationSettings, 
                        manualThermometerCaps: val,
                        // Сохраняем текущие локальные значения
                        additive,
                        weldPriceMarkup,
                        worksTaxPercent,
                        weldCoefficient,
                        thermometerCaps: val,
                        packaging,
                        pipePriceMarkup,
                        mirrorPolishingItems
                      });
                    }}
                    size="small"
                    inputProps={{ step: 1, min: 0 }}
                    sx={{ 
                      width: 60, 
                      '& .MuiInputBase-input': { fontSize, padding: '0.25rem 0.5rem', textAlign: 'center' } 
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize, fontWeight: 500 }}>Упаковка:</Typography>
                  <TextField
                    type="number"
                    value={packaging}
                    onChange={(e) => setPackaging(Number(e.target.value) || 0)}
                    size="small"
                    inputProps={{ step: 1, min: 0 }}
                    sx={{ 
                      width: 60, 
                      '& .MuiInputBase-input': { fontSize, padding: '0.25rem 0.5rem', textAlign: 'center' } 
                    }}
                  />
                </Box>
                
                {/* Слесарка за зеркало - показываем если есть элементы в mirrorPolishingItems */}
                {(() => {
                  // Показываем поля если есть элементы в mirrorPolishingItems
                  if (mirrorPolishingItems.length === 0) {
                    return null;
                  }
                  
                  // Формируем строку с перечислением всех диаметров через запятую
                  const diametersText = mirrorPolishingItems
                    .map(item => `⌀${item.diameter}мм`)
                    .join(', ');
                  
                  // Используем часы из первого элемента (все диаметры будут иметь одинаковое количество часов)
                  const hours = mirrorPolishingItems[0]?.hours || 0;
                  
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize, fontWeight: 500 }}>
                        Слесарка за зеркало {diametersText} (часы):
                      </Typography>
                      <TextField
                        type="number"
                        value={hours}
                        onChange={(e) => {
                          const newHours = Number(e.target.value) || 0;
                          // Обновляем часы для всех диаметров
                          const newItems = mirrorPolishingItems.map(item => ({
                            ...item,
                            hours: newHours
                          }));
                          setMirrorPolishingItems(newItems);
                        }}
                        size="small"
                        inputProps={{ step: 0.5, min: 0 }}
                        sx={{ 
                          width: 60, 
                          '& .MuiInputBase-input': { fontSize, padding: '0.25rem 0.5rem', textAlign: 'center' } 
                        }}
                      />
                    </Box>
                  );
                })()}
              </Box>
              
              <Table size="small" sx={{ '& td, & th': { fontSize, py: 1.5 } }}>
                <TableBody>
                  {/* Комплектующие */}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500 }}>Комплектующие</TableCell>
                    <TableCell align="right">{formatRUB(total)}</TableCell>
                  </TableRow>
                  
                  {/* Резка */}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500 }}>Резка</TableCell>
                    <TableCell align="right">{formatRUB(cuttingTotal)}</TableCell>
                  </TableRow>
                  
                  {/* Общие работы с раскрывающимися подстроками */}
                  <TableRow 
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setWorksExpanded(!worksExpanded)}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {worksExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        <span>Общие работы</span>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }} onClick={(e) => e.stopPropagation()}>
                          <Typography variant="body2" sx={{ fontSize, color: 'text.secondary' }}>Налоги:</Typography>
                          <TextField
                            type="number"
                            value={worksTaxPercent}
                            onChange={(e) => setWorksTaxPercent(Number(e.target.value) || 0)}
                            size="small"
                            sx={{ 
                              width: 55, 
                              '& .MuiInputBase-input': { fontSize, padding: '0.125rem 0.375rem', textAlign: 'center' } 
                            }}
                          />
                          <Typography variant="body2" sx={{ fontSize, color: 'text.secondary' }}>%</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>{formatRUB(totalWorks)}</TableCell>
                  </TableRow>
                  
                  {/* Раскрывающиеся подстроки */}
                  <TableRow>
                    <TableCell colSpan={2} sx={{ p: 0, border: 'none' }}>
                      <Collapse in={worksExpanded}>
                        <Table size="small" sx={{ '& td': { fontSize, py: 1, border: 'none' } }}>
                          <TableBody>
                            <TableRow>
                              <TableCell sx={{ pl: 5, color: 'text.secondary' }}>— Сварка</TableCell>
                              <TableCell align="right" sx={{ color: 'text.secondary' }}>{formatRUB(effectiveWeldTotal)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ pl: 5, color: 'text.secondary' }}>— Прочие работы</TableCell>
                              <TableCell align="right" sx={{ color: 'text.secondary' }}>{formatRUB(otherWorks)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                  
                  {/* Себестоимость */}
                  <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Себестоимость</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {formatRUB(grandTotal)}
                    </TableCell>
                  </TableRow>
                  
                  {/* Слесарка за зеркало (если введены часы) */}
                  {(() => {
                    if (mirrorPolishingItems.length === 0 || !mirrorPolishingItems.some(item => item.hours > 0)) {
                      return null;
                    }
                    
                    // Формируем строку с перечислением всех диаметров
                    const diametersText = mirrorPolishingItems
                      .filter(item => item.hours > 0)
                      .map(item => `⌀${item.diameter}мм`)
                      .join(', ');
                    
                    return (
                      <TableRow>
                        <TableCell>Слесарка за зеркало ({diametersText}) (часы)</TableCell>
                        <TableCell align="right">{formatRUB(mirrorPolishingCost)}</TableCell>
                      </TableRow>
                    );
                  })()}
                  
                  {/* Цена клиента */}
                  <TableRow sx={{ backgroundColor: 'rgba(25, 118, 210, 0.08)' }}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Цена клиента</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'primary.main' }}>
                      {formatRUB(clientPrice)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          );
        })()}

        {/* Вкладка "Комплектующие" */}
        {activeTab === 1 && (
          <Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Наименование</TableCell>
                <TableCell align="right">Кол-во</TableCell>
                <TableCell align="right">Длина, мм</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell align="right">Цена ед.</TableCell>
                <TableCell align="right">Сумма</TableCell>
                <TableCell sx={{ width: 40, p: 0 }} />
              </TableRow>
            </TableHead>
            <TableBody>
            {rows.map((r) => {
              const rowKey = r.key;
              const ov = componentRowOverrides[rowKey] || {};
              // Inline-поле кол-ва: рендерит ячейку кол-ва с редактируемым TextField
              const renderQtyCell = (autoQty) => {
                const currentVal = ov.qty != null ? String(ov.qty) : String(autoQty);
                return (
                  <TableCell align="right" sx={{ p: 0.5 }}>
                    <TextField
                      size="small" variant="outlined" type="number"
                      value={currentVal}
                      onChange={(e) => {
                        const val = e.target.value;
                        const newOv = { ...componentRowOverrides, [rowKey]: { ...ov, qty: val } };
                        saveComponentRowOverrides(newOv);
                      }}
                      sx={{ width: 70 }}
                      inputProps={{ style: { fontSize: 13, padding: '4px 6px' }, min: 0 }}
                    />
                  </TableCell>
                );
              };

              // Кнопка удаления строки (сброс всех overrides для этой строки)
              const renderDeleteCell = () => (
                <TableCell sx={{ p: 0 }}>
                  <Tooltip title="Убрать строку из расчёта">
                    <IconButton size="small" color="error" onClick={() => {
                      const newOv = { ...componentRowOverrides, [rowKey]: { ...ov, hidden: true } };
                      saveComponentRowOverrides(newOv);
                    }}><DeleteIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              );

              // Скрытые строки не показываем
              if (ov.hidden) return null;

              // Эффективное кол-во с учётом ручного переопределения
              const effQty = ov.qty != null ? Number(ov.qty) : r.qty;

              // Комплектующие KRGB (профили)
              if (r.isKRGBProfile) {
                const CUTTING_ALLOWANCE = 100;
                const pricePerMeter = getSquarePipePricePerMeter('80x40', '2', 'Матовый', 'profile');
                const perMeter = typeof pricePerMeter === 'number' ? pricePerMeter : 0;
                const perMm = perMeter / 1000;
                // Пересчитываем длину пропорционально новому кол-ву
                const unitSumLen = r.qty > 0 ? r.sumLength / r.qty : 0;
                const adjSumLen = unitSumLen * effQty;
                const adjTotal = adjSumLen + (effQty * CUTTING_ALLOWANCE);
                const lengthDisplay = `${Math.round(adjSumLen)} + ${Math.round(effQty * CUTTING_ALLOWANCE)} рез.`;
                const amount = adjTotal * perMm;
                return (
                  <TableRow key={rowKey} sx={ov.qty != null ? { background: 'rgba(25,118,210,0.06)' } : {}}>
                    <TableCell>{r.displayName}</TableCell>
                    {renderQtyCell(r.qty)}
                    <TableCell align="right">{lengthDisplay}</TableCell>
                    <TableCell>за мм</TableCell>
                    <TableCell align="right">{formatRUB(perMeter)} / м</TableCell>
                    <TableCell align="right">{formatRUB(amount)}</TableCell>
                    {renderDeleteCell()}
                  </TableRow>
                );
              }
              
              // Комплектующие KRGB (трубы)
              if (r.isKRGBPipe) {
                const KRGB_PIPE_CUTTING = 200;
                const perMeter = getPipePricePerMeter(r.diameter, 'x3', 'Матовый');
                const perMm = perMeter / 1000;
                const unitSumLen = r.qty > 0 ? r.sumLength / r.qty : 0;
                const adjSumLen = unitSumLen * effQty;
                const adjTotal = adjSumLen + (effQty * KRGB_PIPE_CUTTING);
                const lengthDisplay = `${Math.round(adjSumLen)} + ${Math.round(effQty * KRGB_PIPE_CUTTING)} рез.`;
                const amount = adjTotal * perMm;
                return (
                  <TableRow key={rowKey} sx={ov.qty != null ? { background: 'rgba(25,118,210,0.06)' } : {}}>
                    <TableCell>{r.displayName}</TableCell>
                    {renderQtyCell(r.qty)}
                    <TableCell align="right">{lengthDisplay}</TableCell>
                    <TableCell>за мм</TableCell>
                    <TableCell align="right">{formatRUB(perMeter)} / м</TableCell>
                    <TableCell align="right">{formatRUB(amount)}</TableCell>
                    {renderDeleteCell()}
                  </TableRow>
                );
              }
              
              // Дополнительная проверка: если у элемента нет данных в fittingsData и это не труба - пропускаем
              const lookupKey = r.itemKey || r.key;
              if (!r.isPipe && !fittingsData[lookupKey]) {
                return null;
              }
              
              if (r.isPipe) {
                const CUTTING_ALLOWANCE = 100;
                let perMeter = 0;
                let displayName = '';
                let amount = 0;
                let priceDisplay = '';
                let lengthDisplay = '';
                
                if (r.pipeType === 'round') {
                  // Пересчитываем длину под effQty: unitLen * effQty + effQty * резка
                  const unitLen = r.qty > 0 ? r.sumLength / r.qty : 0;
                  const adjSumLen = unitLen * effQty;
                  const adjTotal = adjSumLen + effQty * CUTTING_ALLOWANCE;
                  lengthDisplay = r.unitLength != null
                    ? `${r.unitLength} + ${CUTTING_ALLOWANCE} рез.`
                    : `${Math.round(adjSumLen)} + ${Math.round(effQty * CUTTING_ALLOWANCE)} рез.`;
                  perMeter = getPipePricePerMeter(r.diameter, r.thickness === '-' ? undefined : r.thickness, r.coating === '-' ? undefined : r.coating);
                  displayName = `Труба Ø${r.diameter}`;
                  if (r.coating && r.coating !== '-') displayName += ` ${r.coating}`;
                  if (r.thickness && r.thickness !== '-') displayName += ` ${r.thickness}`;
                  amount = adjTotal * (perMeter / 1000);
                  priceDisplay = `${formatRUB(perMeter)} / м`;
                } else if (r.pipeType === 'square') {
                  const priceInfo = getSquarePipePricePerMeter(r.profile, r.thickness === '-' ? undefined : r.thickness, r.coating === '-' ? undefined : r.coating, r.squareType);
                  const typeLabel = r.squareType === 'sheet' ? 'Лист' : 'Профиль';
                  displayName = `${typeLabel} □${r.profile}`;
                  if (r.coating && r.coating !== '-') displayName += ` ${r.coating}`;
                  if (r.thickness && r.thickness !== '-') displayName += ` ${r.thickness}мм`;
                  if (r.squareType === 'sheet' && typeof priceInfo === 'object' && priceInfo.pricePerMeter) {
                    // Для листов кол-во масштабирует пропорционально
                    const adjLen = r.qty > 0 ? r.sumLength / r.qty * effQty : r.sumLength;
                    lengthDisplay = r.unitLength != null ? `${r.unitLength}` : `${Math.round(adjLen)}`;
                    const lengthInMm = adjLen;
                    const lengthInMeters = lengthInMm / 1000;
                    amount = priceInfo.pricePerMeter * lengthInMeters * (priceInfo.coefficient1 + priceInfo.coefficient2 / lengthInMm);
                    priceDisplay = `${formatRUB(priceInfo.pricePerMeter)}/м × (${priceInfo.coefficient1} + ${priceInfo.coefficient2}/L)`;
                  } else {
                    const unitLen = r.qty > 0 ? r.sumLength / r.qty : 0;
                    const adjSumLen = unitLen * effQty;
                    const adjTotal = adjSumLen + effQty * CUTTING_ALLOWANCE;
                    lengthDisplay = r.unitLength != null
                      ? `${r.unitLength} + ${CUTTING_ALLOWANCE} рез.`
                      : `${Math.round(adjSumLen)} + ${Math.round(effQty * CUTTING_ALLOWANCE)} рез.`;
                    perMeter = typeof priceInfo === 'number' ? priceInfo : 0;
                    amount = adjTotal * (perMeter / 1000);
                    priceDisplay = `${formatRUB(perMeter)} / м`;
                  }
                }
                return (
                  <TableRow key={rowKey} sx={ov.qty != null ? { background: 'rgba(25,118,210,0.06)' } : {}}>
                    <TableCell>{displayName}</TableCell>
                    {renderQtyCell(r.qty)}
                    <TableCell align="right">{lengthDisplay}</TableCell>
                    <TableCell>за мм</TableCell>
                    <TableCell align="right">{priceDisplay}</TableCell>
                    <TableCell align="right">{formatRUB(amount)}</TableCell>
                    {renderDeleteCell()}
                  </TableRow>
                );
              }

              const cfg = effectivePrices[lookupKey] || { pricingType: 'per_piece', price: 0 };
              const displayLength = r.isNippelOrMufta ? r.unitLength : null;
              
              // Обработка ниппелей с формулой
              if (cfg.pricingType === 'nippel_formula') {
                let totalAmount = 0;
                (r.lengths || []).forEach(length => {
                  if (length <= cfg.baseLength) {
                    totalAmount += cfg.minPrice;
                  } else {
                    const pipePricePerMeter = getPipePricePerMeter(
                      cfg.pipeDiameter, cfg.pipeThickness, cfg.pipeCoating || 'Матовый'
                    );
                    const extraLength = length - cfg.baseLength;
                    const pipeCost = (pipePricePerMeter * extraLength) / 1000;
                    const weldCost = cfg.weldCost * cfg.weldCoefficient;
                    totalAmount += cfg.minPrice + pipeCost + weldCost;
                  }
                });
                // Масштабируем пропорционально effQty
                if (r.qty > 0) totalAmount = totalAmount / r.qty * effQty;
                const autoName = formatFittingDisplayName(lookupKey, fittingsData[lookupKey]);
                const dispLen = displayLength !== null ? displayLength : r.sumLength;
                return (
                  <TableRow key={rowKey} sx={ov.qty != null ? { background: 'rgba(25,118,210,0.06)' } : {}}>
                    <TableCell>{autoName}</TableCell>
                    {renderQtyCell(r.qty)}
                    <TableCell align="right">{dispLen}</TableCell>
                    <TableCell>по формуле</TableCell>
                    <TableCell align="right">{formatRUB(cfg.minPrice)} (мин.)</TableCell>
                    <TableCell align="right">{formatRUB(totalAmount)}</TableCell>
                    {renderDeleteCell()}
                  </TableRow>
                );
              }
              
              const perMm = cfg.pricingType === 'per_mm';
              const autoAmount = perMm ? r.sumLength * (cfg.price || 0) : effQty * (cfg.price || 0);
              const autoName = formatFittingDisplayName(lookupKey, fittingsData[lookupKey]);
              const lenDisplay = perMm ? r.sumLength : (displayLength !== null ? displayLength : '-');
              const finalAmount = autoAmount;
              return (
                <TableRow key={rowKey} sx={ov.qty != null ? { background: 'rgba(25,118,210,0.06)' } : {}}>
                  <TableCell>{autoName}</TableCell>
                  {renderQtyCell(r.qty)}
                  <TableCell align="right">{lenDisplay}</TableCell>
                  <TableCell>{perMm ? 'за мм' : 'за шт'}</TableCell>
                  <TableCell align="right">{formatRUB(cfg.price || 0)}{perMm ? ' / мм' : ' / шт'}</TableCell>
                  <TableCell align="right">{formatRUB(finalAmount)}</TableCell>
                  {renderDeleteCell()}
                </TableRow>
              );
            })}

            {/* Дополнительные строки */}
            {extraComponentRows.map((er) => {
              const isEditingExtra = editingExtraId === er.id;
              const qty = Number(er.qty) || 0;
              const len = Number(er.length) || 0;
              const price = Number(er.price) || 0;
              // Длина в мм × кол-во, цена за метр → делим на 1000
              const amount = len > 0 ? (len / 1000) * price * qty : qty * price;
              if (isEditingExtra) {
                return (
                  <TableRow key={er.id}>
                    <TableCell sx={{ p: 0.5 }}>
                      <TextField size="small" variant="outlined" value={editingExtraData.name}
                        onChange={(e) => setEditingExtraData(d => ({ ...d, name: e.target.value }))}
                        sx={{ width: 180 }} inputProps={{ style: { fontSize: 13, padding: '4px 6px' } }} />
                    </TableCell>
                    <TableCell align="right" sx={{ p: 0.5 }}>
                      <TextField size="small" variant="outlined" type="number" value={editingExtraData.qty}
                        onChange={(e) => setEditingExtraData(d => ({ ...d, qty: e.target.value }))}
                        sx={{ width: 70 }} inputProps={{ style: { fontSize: 13, padding: '4px 6px' } }} />
                    </TableCell>
                    <TableCell align="right" sx={{ p: 0.5 }}>
                      <TextField size="small" variant="outlined" type="number" value={editingExtraData.length}
                        onChange={(e) => setEditingExtraData(d => ({ ...d, length: e.target.value }))}
                        sx={{ width: 90 }} inputProps={{ style: { fontSize: 13, padding: '4px 6px' } }} placeholder="мм" />
                    </TableCell>
                    <TableCell sx={{ p: 0.5 }} />
                    <TableCell align="right" sx={{ p: 0.5 }}>
                      <TextField size="small" variant="outlined" type="number" value={editingExtraData.price}
                        onChange={(e) => setEditingExtraData(d => ({ ...d, price: e.target.value }))}
                        sx={{ width: 90 }} inputProps={{ style: { fontSize: 13, padding: '4px 6px' } }} placeholder="₽/м или ₽/шт" />
                    </TableCell>
                    <TableCell align="right" sx={{ p: 0.5 }}>
                      {(() => {
                        const q = Number(editingExtraData.qty) || 0;
                        const l = Number(editingExtraData.length) || 0;
                        const p = Number(editingExtraData.price) || 0;
                        return formatRUB(l > 0 ? (l / 1000) * p * q : q * p);
                      })()}
                    </TableCell>
                    <TableCell sx={{ p: 0, whiteSpace: 'nowrap' }}>
                      <IconButton size="small" color="success" onClick={() => {
                        const updated = extraComponentRows.map(x => x.id === er.id ? { ...x, ...editingExtraData } : x);
                        saveExtraComponentRows(updated);
                        setEditingExtraId(null);
                      }}><CheckIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => setEditingExtraId(null)}><CloseIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                );
              }
              return (
                <TableRow key={er.id} sx={{ background: 'rgba(46,125,50,0.06)' }}>
                  <TableCell>{er.name || '—'}</TableCell>
                  <TableCell align="right">{qty || '—'}</TableCell>
                  <TableCell align="right">{len > 0 ? len : '—'}</TableCell>
                  <TableCell>{len > 0 ? 'за м' : 'за шт'}</TableCell>
                  <TableCell align="right">{formatRUB(price)}{len > 0 ? ' / м' : ' / шт'}</TableCell>
                  <TableCell align="right">{formatRUB(amount)}</TableCell>
                  <TableCell sx={{ p: 0, whiteSpace: 'nowrap' }}>
                    <IconButton size="small" onClick={() => { setEditingExtraId(er.id); setEditingExtraData({ name: er.name || '', qty: er.qty || '', length: er.length || '', price: er.price || '' }); }}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => { saveExtraComponentRows(extraComponentRows.filter(x => x.id !== er.id)); }}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Строка добавления новой */}
            {editingExtraId === 'new' && (
              <TableRow>
                <TableCell sx={{ p: 0.5 }}>
                  <TextField size="small" variant="outlined" value={editingExtraData.name}
                    onChange={(e) => setEditingExtraData(d => ({ ...d, name: e.target.value }))}
                    sx={{ width: 180 }} inputProps={{ style: { fontSize: 13, padding: '4px 6px' } }} placeholder="Наименование" autoFocus />
                </TableCell>
                <TableCell align="right" sx={{ p: 0.5 }}>
                  <TextField size="small" variant="outlined" type="number" value={editingExtraData.qty}
                    onChange={(e) => setEditingExtraData(d => ({ ...d, qty: e.target.value }))}
                    sx={{ width: 70 }} inputProps={{ style: { fontSize: 13, padding: '4px 6px' } }} placeholder="Кол-во" />
                </TableCell>
                <TableCell align="right" sx={{ p: 0.5 }}>
                  <TextField size="small" variant="outlined" type="number" value={editingExtraData.length}
                    onChange={(e) => setEditingExtraData(d => ({ ...d, length: e.target.value }))}
                    sx={{ width: 90 }} inputProps={{ style: { fontSize: 13, padding: '4px 6px' } }} placeholder="Длина, мм" />
                </TableCell>
                <TableCell sx={{ p: 0.5 }} />
                <TableCell align="right" sx={{ p: 0.5 }}>
                  <TextField size="small" variant="outlined" type="number" value={editingExtraData.price}
                    onChange={(e) => setEditingExtraData(d => ({ ...d, price: e.target.value }))}
                    sx={{ width: 90 }} inputProps={{ style: { fontSize: 13, padding: '4px 6px' } }} placeholder="₽/м или ₽/шт" />
                </TableCell>
                <TableCell align="right" sx={{ p: 0.5 }}>
                  {(() => {
                    const q = Number(editingExtraData.qty) || 0;
                    const l = Number(editingExtraData.length) || 0;
                    const p = Number(editingExtraData.price) || 0;
                    return formatRUB(l > 0 ? (l / 1000) * p * q : q * p);
                  })()}
                </TableCell>
                <TableCell sx={{ p: 0, whiteSpace: 'nowrap' }}>
                  <IconButton size="small" color="success" onClick={() => {
                    const newRow = { id: `extra_${Date.now()}`, ...editingExtraData };
                    saveExtraComponentRows([...extraComponentRows, newRow]);
                    setEditingExtraId(null);
                    setEditingExtraData({ name: '', qty: '', length: '', price: '' });
                  }}><CheckIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => { setEditingExtraId(null); setEditingExtraData({ name: '', qty: '', length: '', price: '' }); }}><CloseIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            )}

            <TableRow>
              <TableCell colSpan={5} align="right"><Typography variant="subtitle1">Итого:</Typography></TableCell>
              <TableCell align="right"><Typography variant="subtitle1">{formatRUB(total)}</Typography></TableCell>
              <TableCell sx={{ p: 0 }} />
            </TableRow>
          </TableBody>
        </Table>
        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<AddIcon />} variant="outlined"
            onClick={() => { setEditingExtraId('new'); setEditingExtraData({ name: '', qty: '', length: '', price: '' }); }}>
            Добавить строку
          </Button>
        </Box>
        </Box>
        )}

        {/* Вкладка "Сварка" */}
        {activeTab === 2 && (() => {
          // Фиксированные цены для конкретных диаметров
          const fixedWeldPrices = {
            'Прямая': {
              21.3: 13,
              24.5: 13,
              26.9: 14,
              29.9: 15,
              33.7: 15,
              38: 19.4,
              42.4: 19.4,
              48: 25,
              48.3: 25,
              53.9: 27.6,
              60.3: 27.6,
              66: 34.2,
              76.1: 34.2,
              88.9: 42.3,
              114.3: 43.8,
              129: 61,
              159: 72.5,
              273: 129,
              default: 0.5  // Для всех остальных диаметров - по формуле (₽/мм)
            },
            'Сферическая': {
              21.3: 29,
              24.5: 29,
              26.9: 29,
              29.9: 32,
              33.7: 32,
              38: 34.2,
              42.4: 34.2,
              48: 38,
              48.3: 38,
              53.9: 44.6,
              60.3: 44.6,
              66: 55,
              76.1: 55,
              88.9: 64.8,
              114.3: 80,
              129: 99,
              159: 111,
              273: 198,
              default: 0.85  // Для всех остальных диаметров - по формуле (₽/мм)
            }
          };
          
          const welds = objectsSnapshot.welds || [];
          const cylinders = objectsSnapshot.cylinders || [];
          const markupMultiplier = 1 + (weldPriceMarkup / 100); // Повышающий коэффициент
          
          // Функция для проверки, относится ли сварка к скрытому цилиндру
          const isWeldHidden = (weld) => {
            if (weld.obj1?.objectType === 'cylinder') {
              const cyl = cylinders.find(c => c.id === weld.obj1.objectId);
              if (cyl?.isHidden) return true;
            }
            if (weld.obj2?.objectType === 'cylinder') {
              const cyl = cylinders.find(c => c.id === weld.obj2.objectId);
              if (cyl?.isHidden) return true;
            }
            return false;
          };
          
          // Разделяем сварки: "ПрофильЛист" обрабатывается отдельно
          const profileSheetWelds = welds.filter(w => w.type === 'ПрофильЛист' && !isWeldHidden(w));
          let regularWelds = welds.filter(w => w.type !== 'ПрофильЛист' && !isWeldHidden(w));
          
          // Если есть сварки "ПрофильЛист", объединяем пары сварок профиль+лист
          if (profileSheetWelds.length > 0) {
            const weldsBySize = new Map(); // displayDiameter -> [welds]
            const otherWelds = [];
            
            // Группируем сварки по размеру
            regularWelds.forEach(w => {
              if (w.displayDiameter === '80х40' || w.displayDiameter === '60х30') {
                const key = w.displayDiameter;
                if (!weldsBySize.has(key)) {
                  weldsBySize.set(key, []);
                }
                weldsBySize.get(key).push(w);
              } else {
                otherWelds.push(w);
              }
            });
            
            const newWelds = [...otherWelds];
            
            // Объединяем пары сварок 80х40 -> 80х80
            const welds80x40 = weldsBySize.get('80х40') || [];
            for (let i = 0; i < welds80x40.length; i += 2) {
              if (i + 1 < welds80x40.length) {
                // Есть пара - объединяем в 80х80
                // Периметр квадрата 80х80 = 4*80 = 320
                // Эквивалентный диаметр = периметр / π = 320 / 3.14159 ≈ 101.86
                const perimeter80x80 = 4 * 80;
                const equivalentDiameter80x80 = perimeter80x80 / Math.PI;
                // length для сварок - это эквивалентный диаметр, поэтому используем equivalentDiameter80x80
                newWelds.push({
                  ...welds80x40[i],
                  displayDiameter: '80х80',
                  diameter: equivalentDiameter80x80,
                  length: equivalentDiameter80x80
                });
              } else {
                // Нечетная сварка - оставляем как есть
                newWelds.push(welds80x40[i]);
              }
            }
            
            // Объединяем пары сварок 60х30 -> 60х60
            const welds60x30 = weldsBySize.get('60х30') || [];
            for (let i = 0; i < welds60x30.length; i += 2) {
              if (i + 1 < welds60x30.length) {
                // Есть пара - объединяем в 60х60
                // Периметр квадрата 60х60 = 4*60 = 240
                // Эквивалентный диаметр = периметр / π = 240 / 3.14159 ≈ 76.39
                const perimeter60x60 = 4 * 60;
                const equivalentDiameter60x60 = perimeter60x60 / Math.PI;
                // length для сварок - это эквивалентный диаметр, поэтому используем equivalentDiameter60x60
                newWelds.push({
                  ...welds60x30[i],
                  displayDiameter: '60х60',
                  diameter: equivalentDiameter60x60,
                  length: equivalentDiameter60x60
                });
              } else {
                // Нечетная сварка - оставляем как есть
                newWelds.push(welds60x30[i]);
              }
            }
            
            regularWelds = newWelds;
          }
          
          // Группируем обычные сварки по типу, а затем по displayDiameter (для отображения)
          // displayDiameter может быть строкой "80х80" для квадратных заглушек или числом
          const weldGroups = new Map(); // type -> Map(displayDiameter -> { count, totalLength, numericDiameter })
          regularWelds.forEach(w => {
            const type = w.type || 'unknown';
            const numericDiameter = w.diameter || 0;
            // Используем displayDiameter для группировки, если есть, иначе числовой diameter
            const displayDiameter = w.displayDiameter !== undefined ? w.displayDiameter : numericDiameter;
            
            if (!weldGroups.has(type)) {
              weldGroups.set(type, new Map());
            }
            const typeGroup = weldGroups.get(type);
            
            if (!typeGroup.has(displayDiameter)) {
              typeGroup.set(displayDiameter, { count: 0, totalLength: 0, numericDiameter });
            }
            const diameterGroup = typeGroup.get(displayDiameter);
            diameterGroup.count += 1;
            diameterGroup.totalLength += w.length || 0;
          });
          
          // Создаём строки для таблицы
          const tableRows = [];
          
          // Обычные сварки (с коэффициентами и процентами)
          Array.from(weldGroups.entries()).forEach(([type, typeGroup]) => {
            // Сортируем по числовому диаметру для порядка
            const diameters = Array.from(typeGroup.entries()).sort((a, b) => {
              const numA = typeof a[1].numericDiameter === 'number' ? a[1].numericDiameter : 0;
              const numB = typeof b[1].numericDiameter === 'number' ? b[1].numericDiameter : 0;
              return numA - numB;
            });
            const rowCount = diameters.length;
            
            diameters.forEach(([displayDiameter, data], idx) => {
              const numericDiameter = data.numericDiameter;
              
              // Проверяем, есть ли фиксированная цена для этого диаметра
              const fixedPrice = fixedWeldPrices[type]?.[numericDiameter];
              
              let rowSum;
              let priceInfo;
              
              if (fixedPrice !== undefined) {
                // Фиксированная цена - применяем только повышающий коэффициент, не зависит от длины
                rowSum = fixedPrice * markupMultiplier * data.count;
                priceInfo = `${formatRUB(fixedPrice)}/шт`;
              } else {
                // Расчет по формуле - зависит от длины и повышающего коэффициента
                const pricePerMm = fixedWeldPrices[type]?.default || 0.5;
                rowSum = data.totalLength * pricePerMm * markupMultiplier;
                priceInfo = formatRUB(pricePerMm);
              }
              
              tableRows.push({
                type: idx === 0 ? type : null, // Тип только в первой строке
                pricePerMm: idx === 0 ? priceInfo : null, // Цена только в первой строке
                diameter: displayDiameter, // Для отображения (может быть "80х80" или число)
                count: data.count,
                totalLength: data.totalLength,
                rowSum,
                isFirstInType: idx === 0,
                rowSpan: rowCount,
                isProfileSheet: false,
                isFixed: fixedPrice !== undefined,
              });
            });
          });
          
          // Сварка "ПрофильЛист" - каждая сварка считается как 2 (лист приваривается с двух сторон)
          const profileSheetCount = profileSheetWelds.length * 2; // Умножаем на 2
          const profileSheetTotal = profileSheetCount * 100; // Фиксированная цена 100₽ за сварку
          
          if (profileSheetCount > 0) {
            tableRows.push({
              type: 'ПрофильЛист',
              pricePerMm: null,
              diameter: '-',
              count: profileSheetCount,
              totalLength: null,
              rowSum: profileSheetTotal,
              isFirstInType: true,
              rowSpan: 1,
              isProfileSheet: true,
              fixedPrice: 100, // Фиксированная цена за единицу
            });
          }
          
          // Стили для ячеек с вертикальными линиями
          const cellBorder = { borderRight: '0.0625rem solid rgba(224, 224, 224, 1)' };
          const headerCellStyle = { ...cellBorder, fontWeight: 'bold' };
          
          // Единый стиль шрифта для всей страницы
          const fontSize = '0.875rem'; // 14px
          const labelStyle = { fontSize, whiteSpace: 'nowrap' };
          const inputStyle = { 
            '& .MuiInputBase-input': { fontSize, padding: '0.25rem 0.5rem', textAlign: 'center' },
            width: 55,
          };
          
          return (
            <Box sx={{ fontSize }}>
              {/* Панель настроек сварки */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
                mb: 2,
              }}>
                {/* Основная панель настроек */}
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap',
                  gap: 2,
                  flex: 1,
                  p: 1.5,
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  borderRadius: 1,
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  opacity: manualWeldMode ? 0.4 : 1,
                  pointerEvents: manualWeldMode ? 'none' : 'auto',
                  transition: 'opacity 0.2s',
                }}>
                  {/* Чекбоксы */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={<Checkbox checked={noConduit} onChange={(e) => onCalculationSettingsChange?.({ 
                        ...calculationSettings, 
                        noConduit: e.target.checked,
                        // Сохраняем текущие локальные значения
                        additive,
                        weldPriceMarkup,
                        worksTaxPercent,
                        weldCoefficient,
                        thermometerCaps,
                        packaging,
                        pipePriceMarkup,
                        mirrorPolishingItems
                      })} size="small" />}
                      label={<span style={labelStyle}>Нет кондуктора</span>}
                      sx={{ m: 0 }}
                    />
                    <FormControlLabel
                      control={<Checkbox checked={oversizedClassic} onChange={(e) => onCalculationSettingsChange?.({ 
                        ...calculationSettings, 
                        oversizedClassic: e.target.checked,
                        // Сохраняем текущие локальные значения
                        additive,
                        weldPriceMarkup,
                        worksTaxPercent,
                        weldCoefficient,
                        thermometerCaps,
                        packaging,
                        pipePriceMarkup,
                        mirrorPolishingItems
                      })} size="small" />}
                      label={<span style={labelStyle}>Габариты больше CLASSIC</span>}
                      sx={{ m: 0 }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FormControlLabel
                        control={<Checkbox 
                          checked={finalAdditive && !perWeldAdditiveMode} 
                          disabled={perWeldAdditiveMode}
                          onChange={(e) => {
                            const val = e.target.checked;
                            setAdditive(val);
                            onCalculationSettingsChange?.({ 
                              ...calculationSettings, 
                              manualAdditive: val,
                              additive: val,
                              weldPriceMarkup,
                              worksTaxPercent,
                              weldCoefficient,
                              thermometerCaps,
                              packaging,
                              pipePriceMarkup,
                              mirrorPolishingItems
                            });
                          }} 
                          size="small" 
                        />}
                        label={<span style={labelStyle}>Присадка</span>}
                        sx={{ m: 0 }}
                      />
                      <Tooltip title={perWeldAdditiveMode ? 'Режим присадки по швам активен' : 'Присадка на отдельные швы'}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            if (!perWeldAdditiveMode) {
                              setPerWeldAdditiveMode(true);
                              setAdditive(false);
                              onCalculationSettingsChange?.({
                                ...calculationSettings,
                                manualAdditive: false,
                                additive: false,
                                perWeldAdditiveMode: true,
                                perWeldAdditiveIds,
                                weldPriceMarkup,
                                worksTaxPercent,
                                weldCoefficient,
                                thermometerCaps,
                                packaging,
                                pipePriceMarkup,
                                mirrorPolishingItems
                              });
                            }
                            setPerWeldAdditiveAnchor(e.currentTarget);
                          }}
                          sx={{
                            color: perWeldAdditiveMode ? 'primary.main' : 'text.secondary',
                            bgcolor: perWeldAdditiveMode ? 'primary.light' : 'transparent',
                            '&:hover': { bgcolor: perWeldAdditiveMode ? 'primary.light' : 'action.hover' },
                            width: 22,
                            height: 22,
                          }}
                        >
                          <TuneIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                
                {/* Поля с количествами */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span style={labelStyle}>Фланцы:</span>
                    <TextField type="number" value={flangesCount} onChange={(e) => setFlangesCount(Number(e.target.value) || 0)} size="small" sx={inputStyle} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span style={labelStyle}>Отводы:</span>
                    <TextField type="number" value={elbowsCount} onChange={(e) => setElbowsCount(Number(e.target.value) || 0)} size="small" sx={inputStyle} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span style={labelStyle}>Более 25кг/2м:</span>
                    <TextField 
                      type="number" 
                      value={heavyOrLongCount} 
                      onChange={(e) => {
                        const val = Number(e.target.value) || 0;
                        onCalculationSettingsChange?.({ 
                          ...calculationSettings, 
                          manualHeavyCount: val,
                          // Сохраняем текущие локальные значения
                          additive,
                          weldPriceMarkup,
                          worksTaxPercent,
                          weldCoefficient,
                          thermometerCaps,
                          packaging,
                          pipePriceMarkup,
                          mirrorPolishingItems
                        });
                      }} 
                      size="small" 
                      sx={inputStyle} 
                    />
                  </Box>
                </Box>
              </Box>
              
              {/* Правая колонка: визуализация + ручной ввод */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flexShrink: 0 }}>
                {/* Кнопка визуализации сварок */}
                <Box sx={{ 
                  px: 1,
                  py: 0.5,
                  backgroundColor: 'rgba(255, 0, 0, 0.05)',
                  borderRadius: 1,
                  border: '0.0625rem solid rgba(255, 0, 0, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={showWeldMarkers} 
                        onChange={(e) => onShowWeldMarkersChange?.(e.target.checked)} 
                        size="small"
                        sx={{ color: 'red', '&.Mui-checked': { color: 'red' }, p: 0.5 }}
                      />
                    }
                    label={<span style={{ fontSize: '0.8rem', fontWeight: 500, whiteSpace: 'nowrap' }}>Швы 🔥</span>}
                    sx={{ m: 0 }}
                  />
                </Box>
                {/* Блок ручного ввода суммы сварки */}
                <Box sx={{
                  px: 1,
                  py: 0.5,
                  backgroundColor: manualWeldMode ? 'rgba(255, 152, 0, 0.08)' : 'rgba(0,0,0,0.02)',
                  borderRadius: 1,
                  border: manualWeldMode ? '0.0625rem solid rgba(255, 152, 0, 0.5)' : '0.0625rem solid rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  transition: 'all 0.2s',
                }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={manualWeldMode}
                        onChange={(e) => { setManualWeldMode(e.target.checked); if (!e.target.checked) setManualWeldTotal(0); }}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#e65100' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#e65100' },
                        }}
                      />
                    }
                    label={<span style={{ fontSize: '0.75rem', fontWeight: 600, color: manualWeldMode ? '#e65100' : 'text.secondary', whiteSpace: 'nowrap' }}>Вручную</span>}
                    sx={{ m: 0 }}
                  />
                  <TextField
                    type="number"
                    value={manualWeldTotal}
                    onChange={(e) => setManualWeldTotal(Number(e.target.value) || 0)}
                    disabled={!manualWeldMode}
                    size="small"
                    placeholder="Сумма ₽"
                    inputProps={{ min: 0, style: { textAlign: 'center', padding: '0.2rem 0.3rem', fontSize: '0.8rem' } }}
                    sx={{ width: '100%' }}
                  />
                </Box>
              </Box>
            </Box>
              
            <Box sx={{ opacity: manualWeldMode ? 0.4 : 1, pointerEvents: manualWeldMode ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
              <Table size="small" sx={{ '& td, & th': { fontSize, borderRight: '0.0625rem solid rgba(224, 224, 224, 1)', py: 1 }, '& td:last-child, & th:last-child': { borderRight: 'none' } }}>
                <TableHead sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                  <TableRow>
                    <TableCell sx={headerCellStyle}>Тип сварки</TableCell>
                    <TableCell align="right" sx={headerCellStyle}>Цена/мм</TableCell>
                    <TableCell align="center" sx={headerCellStyle}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <span>Пов.Цен [</span>
                        <TextField
                          type="number"
                          value={weldPriceMarkup}
                          onChange={(e) => setWeldPriceMarkup(Number(e.target.value) || 0)}
                          size="small"
                          sx={{ width: 45, '& .MuiInputBase-input': { fontSize, padding: '0.125rem 0.25rem', textAlign: 'center' } }}
                        />
                        <span>]%</span>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={headerCellStyle}>Ø, мм</TableCell>
                    <TableCell align="right" sx={headerCellStyle}>Швов</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Сумма</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {tableRows.map((row, idx) => (
                  <TableRow key={idx}>
                    {row.isFirstInType ? (
                      <TableCell rowSpan={row.rowSpan} sx={{ verticalAlign: 'top', fontWeight: 'bold', ...cellBorder }}>
                        {row.type}
                      </TableCell>
                    ) : null}
                    {row.isFirstInType ? (
                      <TableCell rowSpan={row.rowSpan} align="right" sx={{ verticalAlign: 'top', ...cellBorder }}>
                        {row.isProfileSheet ? '100₽/шт' : row.pricePerMm}
                      </TableCell>
                    ) : null}
                    {row.isFirstInType ? (
                      <TableCell rowSpan={row.rowSpan} align="center" sx={{ verticalAlign: 'top', ...cellBorder }}>
                        {row.isProfileSheet ? '-' : `${weldPriceMarkup}%`}
                      </TableCell>
                    ) : null}
                    <TableCell align="right" sx={cellBorder}>{row.isProfileSheet ? '-' : (row.diameter ? row.diameter : '-')}</TableCell>
                    <TableCell align="right" sx={cellBorder}>{row.count}</TableCell>
                    <TableCell align="right">{formatRUB(row.rowSum)}</TableCell>
                  </TableRow>
                ))}
                {tableRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">Сварки не обнаружены</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </Box>
            {/* Итоговая строка — всегда видна, не затемняется */}
            {tableRows.length > 0 && (() => {
              const finalTotal = manualWeldMode ? manualWeldTotal : weldTotal;
              return (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 2,
                  borderTop: '1px solid rgba(224, 224, 224, 1)',
                  py: 1,
                  px: 1,
                }}>
                  {manualWeldMode ? (
                    <span style={{ fontSize, color: '#e65100', fontStyle: 'italic' }}>Ручной ввод</span>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize }}>Коэффициент:</span>
                      <TextField
                        type="number"
                        value={weldCoefficient}
                        onChange={(e) => setWeldCoefficient(Number(e.target.value) || 1)}
                        size="small"
                        inputProps={{ step: 0.1, min: 0, style: { textAlign: 'center', padding: '0.25rem 0.5rem', fontSize } }}
                        sx={{ width: 60 }}
                      />
                    </Box>
                  )}
                  <span style={{ fontSize }}>Итого:</span>
                  <Typography variant="subtitle1" sx={{ fontSize, fontWeight: 600, color: manualWeldMode ? '#e65100' : 'inherit', minWidth: 90, textAlign: 'right' }}>
                    {formatRUB(finalTotal)}
                  </Typography>
                </Box>
              );
            })()}
            </Box>
          );
        })()}

        {/* Вкладка "Резка" */}
        {activeTab === 3 && (() => {
          // Категории резки по диаметру
          // Для малых диаметров (до 168.3) — цена за 1 РЕЗ
          // Для средних диаметров (168.3-200) — цена за 1 РЕЗ
          // Для больших диаметров (от 200) — цена за ТРУБУ (независимо от количества резов)
          const getCuttingPrice = (diameter) => {
            const d = Number(diameter) || 0;
            if (d <= 48.3) return 30;
            if (d <= 60.3) return 40;
            if (d <= 168.3) return 70;
            if (d > 168.3 && d < 200) return 1000;
            if (d >= 200 && d <= 300) return 1500;
            if (d > 300 && d <= 400) return 2000;
            if (d > 400 && d <= 550) return 3500;
            if (d > 550 && d <= 650) return 5000;
            return 0; // Диаметр вне категорий
          };
          
          const getCuttingCategory = (diameter) => {
            const d = Number(diameter) || 0;
            if (d <= 48.3) return 'до 48.3';
            if (d <= 60.3) return 'до 60.3';
            if (d <= 168.3) return 'до 168.3';
            if (d > 168.3 && d < 200) return '168.3-200';
            if (d >= 200 && d <= 300) return '200-300';
            if (d > 300 && d <= 400) return '300-400';
            if (d > 400 && d <= 550) return '400-550';
            if (d > 550 && d <= 650) return '550-650';
            return '-';
          };
          
          // Проверка: это категория с ценой за трубу (а не за рез)?
          const isPricePerPipe = (diameter) => {
            const d = Number(diameter) || 0;
            return d >= 200;
          };
          
          const welds = objectsSnapshot.welds || [];
          const cylinders = objectsSnapshot.cylinders || [];
          
          // Группируем трубы по диаметру (круглые трубы и квадратные профили)
          // Для каждой трубы: 2 базовых реза для труб ≤ 168.3 мм, 1 базовый рез для труб > 168.3 мм
          const pipeGroups = new Map(); // diameter -> { pipes: [], baseCuts: count*2 or count*1 }
          
          cylinders.forEach(c => {
            if (c.isHidden) { return; } // Исключаем скрытые цилиндры (соединительные трубы)
            const ps = c.pipeSpec;
            if (ps?.shape === 'round' && ps.diameter) {
              const diameter = Number(ps.diameter);
              if (!pipeGroups.has(diameter)) {
                pipeGroups.set(diameter, { pipes: [], baseCuts: 0 });
              }
              const group = pipeGroups.get(diameter);
              group.pipes.push(c);
              // ОГРАНИЧЕНИЕ 2: У труб больше 168.3 только 1 базовый рез (вместо 2)
              group.baseCuts += diameter > 168.3 ? 1 : 2;
            }
            // Для квадратных профилей вычисляем эквивалентный диаметр через периметр
            // Формула: периметр = 2*(a+b), эквивалентный диаметр = периметр / π
            if (ps?.shape === 'square' && ps.profile) {
              const parts = String(ps.profile).split('x');
              if (parts.length === 2) {
                const a = Number(parts[0]) || 0;
                const b = Number(parts[1]) || 0;
                const perimeter = 2 * (a + b); // Периметр профиля
                const diameter = perimeter / Math.PI; // Эквивалентный диаметр
                if (!pipeGroups.has(diameter)) {
                  pipeGroups.set(diameter, { pipes: [], baseCuts: 0 });
                }
                const group = pipeGroups.get(diameter);
                group.pipes.push(c);
                // Для листов (squareType: 'sheet') не добавляем базовые резки
                if (ps.squareType !== 'sheet') {
                  // ОГРАНИЧЕНИЕ 2: У профилей больше 168.3 только 1 базовый рез (вместо 2)
                  group.baseCuts += diameter > 168.3 ? 1 : 2;
                }
              }
            }
          });
          
          // Резы от сварок считаются по МЕНЬШЕМУ диаметру соединения
          // w.diameter уже содержит меньший диаметр из двух соединяемых объектов
          // ВАЖНО: рез считается только если соединение на ТОРЦЕ трубы, а не на боковой поверхности
          const weldCutsByDiameter = new Map(); // diameter -> count
          
          // Функция для проверки, является ли соединение БОКОВЫМ (не торцевым) для трубы
          // Боковое соединение = нужно вырезать отверстие = рез
          // Торцевое соединение = труба и так отрезана = НЕ рез
          const isSideConnection = (weld, cylinder) => {
            if (!cylinder || !weld) return false;
            
            const ps = cylinder.pipeSpec;
            if (!ps) return false;
            
            const pipeWidth = cylinder.width || 0;
            const pipeHeight = cylinder.height || 0;
            const pipeX = cylinder.x || 0;
            const pipeY = cylinder.y || 0;
            
            // Определяем размер трубы/профиля для проверки ориентации
            // Для круглых труб - диаметр, для квадратных - меньшая сторона (реальный размер в Canvas)
            let pipeSize = 0;
            if (ps.shape === 'round') {
              pipeSize = Number(ps.diameter) || 0;
            } else if (ps.shape === 'square' && ps.profile) {
              const parts = String(ps.profile).split('x');
              if (parts.length === 2) {
                const a = Number(parts[0]) || 0;
                const b = Number(parts[1]) || 0;
                // Используем меньшую сторону для проверки ориентации (реальный размер в Canvas)
                pipeSize = Math.min(a, b);
              }
            }
            
            if (pipeSize === 0) return false;
            
            // Определяем ориентацию трубы по соотношению размеров
            // Если width ≈ size — труба вертикальная (по Y)
            // Если height ≈ size — труба горизонтальная (по X)
            const isVertical = Math.abs(pipeWidth - pipeSize) < 5; // размер по X, длина по Y
            const isHorizontal = Math.abs(pipeHeight - pipeSize) < 5; // размер по Y, длина по X
            
            const weldX = weld.x || 0;
            const weldY = weld.y || 0;
            
            // Порог для определения края (торца): фиксированное значение,
            // чтобы для больших труб контуры вблизи краёв не считались торцевыми
            const EDGE_THRESHOLD = Math.min(pipeSize * 0.6, 50);
            
            if (isHorizontal) {
              // Труба горизонтальная (лежит по X)
              // Торцы = левый/правый край → НЕ рез
              // Боковое = где-то посередине по X → рез
              const distToLeft = Math.abs(weldX - pipeX);
              const distToRight = Math.abs(weldX - (pipeX + pipeWidth));
              const isAtEnd = distToLeft < EDGE_THRESHOLD || distToRight < EDGE_THRESHOLD;
              return !isAtEnd; // Боковое = НЕ на торце
            }
            
            if (isVertical) {
              // Труба вертикальная (лежит по Y)
              // Торцы = верхний/нижний край → НЕ рез
              // Боковое = где-то посередине по Y → рез
              const distToTop = Math.abs(weldY - pipeY);
              const distToBottom = Math.abs(weldY - (pipeY + pipeHeight));
              const isAtEnd = distToTop < EDGE_THRESHOLD || distToBottom < EDGE_THRESHOLD;
              return !isAtEnd; // Боковое = НЕ на торце
            }
            
            // Если не определили ориентацию, считаем что это боковое соединение (рез)
            return true;
          };
          
          welds.forEach(w => {
            // Пропускаем сварки типа "ПрофильЛист" - они не создают резов на круглых трубах
            if (w.type === 'ПрофильЛист') return;
            
            // Проверяем, что оба объекта сварки не скрытые
            const cyl1 = w.obj1?.objectType === 'cylinder' ? cylinders.find(c => c.id === w.obj1.objectId) : null;
            const cyl2 = w.obj2?.objectType === 'cylinder' ? cylinders.find(c => c.id === w.obj2.objectId) : null;
            
            // Если хотя бы один из объектов скрытый - пропускаем
            if ((cyl1 && cyl1.isHidden) || (cyl2 && cyl2.isHidden)) {
              return;
            }
            
            // ОГРАНИЧЕНИЕ 1: Когда к трубе больше 168.3 привариваются трубы больше 168.3 - резы не считаются
            // Получаем диаметры обеих труб
            const getDiameter = (cyl) => {
              if (!cyl || !cyl.pipeSpec) return 0;
              if (cyl.pipeSpec.shape === 'round') {
                return Number(cyl.pipeSpec.diameter) || 0;
              } else if (cyl.pipeSpec.shape === 'square' && cyl.pipeSpec.profile) {
                // Для квадратных профилей вычисляем эквивалентный диаметр
                const parts = String(cyl.pipeSpec.profile).split('x');
                if (parts.length === 2) {
                  const a = Number(parts[0]) || 0;
                  const b = Number(parts[1]) || 0;
                  const perimeter = 2 * (a + b);
                  return perimeter / Math.PI;
                }
              }
              return 0;
            };
            
            // Проверяем каждый объект сварки
            let countsCut = false;
            let hasSideConnection = false;
            
            // Проверяем obj1
            if (w.obj1?.objectType === 'cylinder') {
              const cyl = cylinders.find(c => c.id === w.obj1.objectId);
              if (cyl && !cyl.isHidden && (cyl.pipeSpec?.shape === 'round' || cyl.pipeSpec?.shape === 'square')) {
                if (isSideConnection(w, cyl)) {
                  countsCut = true;
                  hasSideConnection = true;
                }
              }
            }
            
            // Проверяем obj2
            if (w.obj2?.objectType === 'cylinder') {
              const cyl = cylinders.find(c => c.id === w.obj2.objectId);
              if (cyl && !cyl.isHidden && (cyl.pipeSpec?.shape === 'round' || cyl.pipeSpec?.shape === 'square')) {
                if (isSideConnection(w, cyl)) {
                  countsCut = true;
                  hasSideConnection = true;
                }
              }
            }
            
            // ОГРАНИЧЕНИЕ 1: Когда обе трубы > 168.3 мм и соединение ТОРЦЕВОЕ - резы не считаются
            // Для боковых соединений (отверстие в стенке) рез нужен всегда
            if (!hasSideConnection) {
              const diameter1 = getDiameter(cyl1);
              const diameter2 = getDiameter(cyl2);
              if (diameter1 > 168.3 && diameter2 > 168.3) {
                return;
              }
            }
            
            // Специальная обработка для дополнительных выходов гидроразделителя
            // Они всегда создают боковое соединение (отверстие в стенке трубы)
            // Горизонтальные выходы: source === 'hydraulicSeparatorOutlet'
            // Вертикальные выходы: source === 'selfFacing' + один из объектов - fitting с isCenterFitting
            if (w.source === 'hydraulicSeparatorOutlet') {
              countsCut = true;
            } else if (w.source === 'selfFacing') {
              // Проверяем, что это выход гидроразделителя (центральный фитинг)
              const allFittings = objectsSnapshot.fittings || [];
              const fitting1 = w.obj1?.objectType === 'fitting' ? allFittings.find(f => f.id === w.obj1.objectId) : null;
              const fitting2 = w.obj2?.objectType === 'fitting' ? allFittings.find(f => f.id === w.obj2.objectId) : null;
              
              if ((fitting1 && fitting1.isCenterFitting) || (fitting2 && fitting2.isCenterFitting)) {
                countsCut = true;
              }
            }
            
            // Если соединение боковое хотя бы для одной трубы - считаем рез
            if (countsCut) {
              const weldDiameter = Number(w.diameter) || 0;
              if (weldDiameter > 0) {
                weldCutsByDiameter.set(weldDiameter, (weldCutsByDiameter.get(weldDiameter) || 0) + 1);
              }
            }
          });
          
          // Группируем резы по категориям
          // Для малых диаметров (до 168.3) — считаем РЕЗЫ
          // Для больших диаметров (от 200) — считаем ТРУБЫ
          const cutsByCategory = new Map(); // category -> { count: number, price: number, isPerPipe: boolean }
          
          // Для больших труб: считаем количество ТРУБ по категориям
          const largePipesByCategory = new Map(); // category -> Set of pipe IDs
          
          // Добавляем базовые резы (по диаметрам труб)
          Array.from(pipeGroups.entries()).forEach(([diameter, data]) => {
            const price = getCuttingPrice(diameter);
            const category = getCuttingCategory(diameter);
            const perPipe = isPricePerPipe(diameter);
            
            if (perPipe) {
              // Большие трубы — считаем количество труб
              if (!largePipesByCategory.has(category)) {
                largePipesByCategory.set(category, new Set());
              }
              data.pipes.forEach(pipe => largePipesByCategory.get(category).add(pipe.id));
            } else {
              // Малые трубы — считаем резы
              if (!cutsByCategory.has(category)) {
                cutsByCategory.set(category, { count: 0, price, isPerPipe: false });
              }
              cutsByCategory.get(category).count += data.baseCuts;
            }
          });
          
          // Добавляем резы от сварок (по меньшему диаметру соединения)
          // Для больших труб — НЕ добавляем (цена за трубу уже включает все резы)
          Array.from(weldCutsByDiameter.entries()).forEach(([diameter, cuts]) => {
            const price = getCuttingPrice(diameter);
            const category = getCuttingCategory(diameter);
            const perPipe = isPricePerPipe(diameter);
            
            if (!perPipe) {
              // Малые диаметры — считаем резы
              if (!cutsByCategory.has(category)) {
                cutsByCategory.set(category, { count: 0, price, isPerPipe: false });
              }
              cutsByCategory.get(category).count += cuts;
            }
            // Для больших диаметров резы от сварок не добавляются — цена уже за трубу
          });
          
          // Добавляем виртуальные резы из массива cuts (например, от KRGB крепежей)
          const virtualCuts = objectsSnapshot.cuts || [];
          virtualCuts.forEach(cut => {
            const diameter = Number(cut.diameter) || 0;
            if (diameter > 0) {
              const price = getCuttingPrice(diameter);
              const category = getCuttingCategory(diameter);
              const perPipe = isPricePerPipe(diameter);
              
              if (!perPipe) {
                // Малые диаметры — считаем резы
                if (!cutsByCategory.has(category)) {
                  cutsByCategory.set(category, { count: 0, price, isPerPipe: false });
                }
                cutsByCategory.get(category).count += 1; // Каждый cut = 1 рез
              }
            }
          });
          
          // Добавляем большие трубы в общую карту
          Array.from(largePipesByCategory.entries()).forEach(([category, pipeIds]) => {
            const sampleDiameter = category === '200-300' ? 200 : 
                                   category === '300-400' ? 301 : 
                                   category === '400-550' ? 401 : 
                                   category === '550-650' ? 551 : 200;
            const price = getCuttingPrice(sampleDiameter);
            
            cutsByCategory.set(category, { 
              count: pipeIds.size, 
              price, 
              isPerPipe: true 
            });
          });
          
          // Создаём строки для таблицы
          const cuttingRows = [];
          let totalCuttingCost = 0;
          
          // Сортируем категории по цене (от меньшей к большей)
          Array.from(cutsByCategory.entries())
            .sort((a, b) => a[1].price - b[1].price)
            .forEach(([category, data]) => {
              const rowSum = data.count * data.price;
              totalCuttingCost += rowSum;
              
              cuttingRows.push({
                category,
                count: data.count,
                price: data.price,
                isPerPipe: data.isPerPipe,
                rowSum,
              });
            });
          
          // Стили для ячеек
          const fontSize = '0.875rem';
          const cellBorder = { borderRight: '0.0625rem solid rgba(224, 224, 224, 1)' };
          const headerCellStyle = { ...cellBorder, fontWeight: 'bold' };
          
          return (
            <Box sx={{ fontSize }}>
              <Table size="small" sx={{ '& td, & th': { fontSize, borderRight: '0.0625rem solid rgba(224, 224, 224, 1)', py: 1 }, '& td:last-child, & th:last-child': { borderRight: 'none' } }}>
                <TableHead sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                  <TableRow>
                    <TableCell sx={headerCellStyle}>Категория</TableCell>
                    <TableCell align="right" sx={headerCellStyle}>Кол-во</TableCell>
                    <TableCell align="right" sx={headerCellStyle}>Цена</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Сумма</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cuttingRows.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={cellBorder}>{row.category}</TableCell>
                      <TableCell align="right" sx={cellBorder}>
                        {row.count} {row.isPerPipe ? 'труб' : 'резов'}
                      </TableCell>
                      <TableCell align="right" sx={cellBorder}>
                        {formatRUB(row.price)}{row.isPerPipe ? '/труба' : '/рез'}
                      </TableCell>
                      <TableCell align="right">{formatRUB(row.rowSum)}</TableCell>
                    </TableRow>
                  ))}
                  {cuttingRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="text.secondary">Трубы для резки не обнаружены</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {cuttingRows.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="right" sx={cellBorder}>
                        <Typography variant="subtitle1" sx={{ fontSize }}>Итого:</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle1" sx={{ fontSize }}>{formatRUB(totalCuttingCost)}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          );
        })()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>

      {/* Popover выбора швов для присадки */}
      <Popover
        open={Boolean(perWeldAdditiveAnchor)}
        anchorEl={perWeldAdditiveAnchor}
        onClose={() => setPerWeldAdditiveAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { minWidth: 320, maxWidth: 440, maxHeight: 480, display: 'flex', flexDirection: 'column' } }}
      >
        {(() => {
          const allWelds = objectsSnapshot.welds || [];
          const allCylinders = objectsSnapshot.cylinders || [];
          const isWeldHiddenLocal = (weld) => {
            if (weld.obj1?.objectType === 'cylinder') {
              const cyl = allCylinders.find(c => c.id === weld.obj1.objectId);
              if (cyl?.isHidden) return true;
            }
            if (weld.obj2?.objectType === 'cylinder') {
              const cyl = allCylinders.find(c => c.id === weld.obj2.objectId);
              if (cyl?.isHidden) return true;
            }
            return false;
          };
          const visibleWelds = allWelds.filter(w => !isWeldHiddenLocal(w));

          const getWeldLabel = (w, idx) => {
            const type = w.type || 'Прямая';
            const diam = w.displayDiameter !== undefined ? w.displayDiameter : (w.diameter || '?');
            return `${idx + 1}. ${type} — Ø${diam} мм`;
          };

          const allSelected = visibleWelds.length > 0 && visibleWelds.every(w => perWeldAdditiveIds.includes(w.id));
          const someSelected = visibleWelds.some(w => perWeldAdditiveIds.includes(w.id));

          const toggleWeld = (id) => {
            const next = perWeldAdditiveIds.includes(id)
              ? perWeldAdditiveIds.filter(x => x !== id)
              : [...perWeldAdditiveIds, id];
            setPerWeldAdditiveIds(next);
            onCalculationSettingsChange?.({
              ...calculationSettings,
              perWeldAdditiveMode: true,
              perWeldAdditiveIds: next,
              manualAdditive: false,
              additive: false,
              weldPriceMarkup,
              worksTaxPercent,
              weldCoefficient,
              thermometerCaps,
              packaging,
              pipePriceMarkup,
              mirrorPolishingItems
            });
          };

          const toggleAll = () => {
            const next = allSelected ? [] : visibleWelds.map(w => w.id);
            setPerWeldAdditiveIds(next);
            onCalculationSettingsChange?.({
              ...calculationSettings,
              perWeldAdditiveMode: true,
              perWeldAdditiveIds: next,
              manualAdditive: false,
              additive: false,
              weldPriceMarkup,
              worksTaxPercent,
              weldCoefficient,
              thermometerCaps,
              packaging,
              pipePriceMarkup,
              mirrorPolishingItems
            });
          };

          const disablePerWeldMode = () => {
            setPerWeldAdditiveMode(false);
            setPerWeldAdditiveIds([]);
            setPerWeldAdditiveAnchor(null);
            onCalculationSettingsChange?.({
              ...calculationSettings,
              perWeldAdditiveMode: false,
              perWeldAdditiveIds: [],
              weldPriceMarkup,
              worksTaxPercent,
              weldCoefficient,
              thermometerCaps,
              packaging,
              pipePriceMarkup,
              mirrorPolishingItems
            });
          };

          return (
            <>
              <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Присадка на отдельные швы</Typography>
                  {someSelected && (
                    <Chip label={`${perWeldAdditiveIds.length}`} size="small" color="primary" sx={{ height: 18, fontSize: 11 }} />
                  )}
                </Box>
                <Button size="small" color="error" onClick={disablePerWeldMode} sx={{ fontSize: 11, py: 0 }}>
                  Выключить
                </Button>
              </Box>
              {visibleWelds.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">Активные швы не обнаружены</Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ px: 1, py: 0.5, borderBottom: '1px solid rgba(0,0,0,0.08)', flexShrink: 0 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected && !allSelected}
                          onChange={toggleAll}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2" sx={{ fontWeight: 500 }}>Выбрать все ({visibleWelds.length})</Typography>}
                      sx={{ m: 0 }}
                    />
                  </Box>
                  <List dense disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
                    {visibleWelds.map((w, idx) => (
                      <ListItem
                        key={w.id}
                        disablePadding
                        sx={{ px: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        onClick={() => toggleWeld(w.id)}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Checkbox
                            edge="start"
                            checked={perWeldAdditiveIds.includes(w.id)}
                            size="small"
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={getWeldLabel(w, idx)}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Box sx={{ p: 1, borderTop: '1px solid rgba(0,0,0,0.08)', flexShrink: 0 }}>
                    <Button
                      size="small"
                      fullWidth
                      variant="contained"
                      onClick={() => setPerWeldAdditiveAnchor(null)}
                      sx={{ fontSize: 12 }}
                    >
                      Применить
                    </Button>
                  </Box>
                </>
              )}
            </>
          );
        })()}
      </Popover>
    </Dialog>
  );
}

export default PriceDialog;

